import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** The Elizabeth Tower (Big Ben) in metres: where it stands, its width and height. */
// (owner: everything farther off) the tower moved out to keep its place on screen, clear of the words
const TOWER = { x: 181, z: 165, w: 12, h: 96 };
/** The Palace of Westminster's front at the tower's right: how far off and how long (m). */
const PALACE = { z: 205, len: 160 };
/** The K6 telephone box on the pavement at the left (m). */
// (owner: the boxes larger, in keeping with the lamp, and a little nearer; then larger again, and the box in
// its real size beside the lamp)
// (owner: the box and the lamp 50 px to the left; then both a little farther back, smaller)
const BOX = { x: -3.88, z: 10.3 };
/** The road across the view (m from us): its near and far kerbs. */
// (owner: the cars too near) the road farther off across the view
const ROAD = { near: 30, far: 44 };

/** A raindrop, in page pixels. */
interface Drop {
  x: number;
  y: number;
  len: number;
  speed: number;
  rank: number;
  alpha: number;
}

/** A vehicle on the road: which lane, where, how fast, what it is (0 a black cab, 1 a red bus, 2 a car). */
interface Vehicle {
  lane: number;
  x: number;
  dir: 1 | -1;
  speed: number;
  kind: number;
  col: number;
}

/**
 * London's hero board (2026-10-18), after the owner's photograph by Westminster at night: in true perspective
 * (a camera on the pavement, everything in metres), the Elizabeth Tower floodlit gold with its glowing clock
 * faces and its belfry lit yellow (owner), the Palace of Westminster's Gothic front running away to the right with its
 * pinnacles and lit windows; a red K6 telephone box on the pavement at the left, clear of the tower (owner);
 * Victorian lamps, bollards and the wet road where the cars' lights stream past; the flagstones shining with
 * the lights' reflections. A light rain falls, heavy on the charge (owner); the page's wallpaper is the rain.
 * Sized by the stage's height.
 */
@Component( {
  selector: 'app-hero-london',
  template: '<canvas #canvas class="hlon-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hlon-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroLondonComponent extends HeroMotifBase {

  /** The still scene behind the traffic: sky, tower, palace, the road. */
  private back: HTMLCanvasElement | null = null;
  /** The same scene with most of the city's windows lit, faded in on the charge. */
  private backLit: HTMLCanvasElement | null = null;
  /** What stands in front of the road: the bollards, the lamps, the telephone box, the pavement. */
  private front: HTMLCanvasElement | null = null;
  /** The spot round the lamp and the box with the lamp lit, the box lit, and both lit, shown over the front. */
  private litLamp: HTMLCanvasElement | null = null;
  private litBox: HTMLCanvasElement | null = null;
  private litBoth: HTMLCanvasElement | null = null;
  /** Where the lamp's light and the box's light fall (page pixels), the spot holding both, how lit each is. */
  private lampRect: [ number, number, number, number ] = [ 0, 0, 0, 0 ];
  private boxRect: [ number, number, number, number ] = [ 0, 0, 0, 0 ];
  private litRect: [ number, number, number, number ] = [ 0, 0, 0, 0 ];
  private lampOn = 0;
  private boxOn = 0;
  private drops: Drop[] = [];
  private cars: Vehicle[] = [];
  private scaleK = 1;
  private eye = 2.5;
  private F = 0;
  /** The telephone box's and the pillar box's own scale, kept as it was. */
  private FB = 0;
  /** The front layer's scale: the pavement, the bollards and the lamps. */
  private Ff = 0;
  private hz = 0;
  private vx = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
  }

  /** Project a point: x across (metres), y up, z away from us. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.vx + this.F * x / z, this.hz - this.F * ( y - this.eye ) / z ];
  }

  protected build(): void {
    const W = this.width, H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    // (owner: all but the telephone box and the pillar box 15% smaller, farther off)
    // (owner: then everything 20% smaller once more, the whole scene farther off)
    // (owner: and 10% smaller once more)
    this.F = 1.15 * H * 0.85 * 0.8 * 0.8 * 0.9 * 0.9 * 0.9;
    // (owner: then all but the things in front 20% smaller again) the front layer keeps its own scale
    this.Ff = 1.15 * H * 0.85 * 0.8 * 0.9 * 0.9 * 0.9;
    this.FB = 1.15 * H * 0.8 * 0.9 * 0.9 * 0.9 * 1.1 * 1.25 * 1.1;
    this.hz = 0.7 * H;
    this.vx = W / 2;
    this.back = null;
    const r = this.seededLocal( 43 ), k = this.scaleK;
    this.drops = [];
    const n = Math.max( 500, Math.round( W * H / 1200 ) );
    for ( let i = 0; i < n; i++ ) {
      this.drops.push( { x: r() * W, y: r() * H, len: ( 14 + r() * 22 ) * k, speed: ( 800 + r() * 500 ) * k, rank: r(), alpha: 0.12 + r() * 0.22 } );
    }
    // (owner: real traffic, not streaks of light) London's black cabs, a red double decker, a few cars
    this.cars = [];
    // (owner: each in its own lane, or they run through each other) eight lanes, the near four going right
    for ( let i = 0; i < 8; i++ ) {
      const lane = 31 + i * 1.75, near = lane < 37;
      this.cars.push( { lane, x: -120 + r() * 240, dir: near ? 1 : -1, speed: 7 + r() * 5, kind: i === 5 ? 1 : r() < 0.55 ? 0 : 2, col: Math.floor( r() * 4 ) } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width, H = this.height;
    if ( this.frozen ) {
      // (owner) the page's wallpaper is the rain
      this.drawWallRain( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a, true ); this.bake( ctx.getTransform().a ); }
    if ( !this.back || !this.front ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    // the windows lighting up across the city on the charge
    const e = this.energy, lift = e * e * ( 3 - 2 * e );
    if ( this.backLit && lift > 0.01 ) { ctx.globalAlpha = lift; ctx.drawImage( this.backLit, 0, 0, W, H ); ctx.globalAlpha = 1; }
    this.drawTraffic( ctx, dt );
    ctx.drawImage( this.front, 0, 0, W, H );
    // (owner: the street lamp and the telephone box lit on the charge; at rest they flicker now and then;
    // then the telephone box lit all the time, the lamp still flickering)
    const now = performance.now() / 1000, k = dt > 0 ? Math.min( 1, dt * 25 ) : 1;
    this.lampOn += ( Math.max( lift, this.flicker( now, 1 ) ) - this.lampOn ) * k;
    this.boxOn = 1;
    {
      // both lit as far as both are, then the one lit more on its own
      const a = Math.min( 1, this.lampOn ), b = Math.min( 1, this.boxOn ), both = Math.min( a, b ), [ ux, uy, uw, uh ] = this.litRect;
      const layers: [ HTMLCanvasElement | null, number ][] = [ [ this.litBoth, both ], [ this.litLamp, a - both ], [ this.litBox, b - both ] ];
      for ( const [ c, alpha ] of layers ) {
        if ( !c || alpha < 0.01 || uw <= 0 || uh <= 0 ) { continue; }
        ctx.globalAlpha = alpha;
        ctx.drawImage( c, 0, 0, c.width, c.height, ux, uy, uw, uh );
      }
      ctx.globalAlpha = 1;
    }
    this.drawRain( ctx, dt );
  }

  /**
   * A faulty lamp's flicker (0 off, 1 lit): off most of the time, now and then a burst of quick flashes, each
   * lamp on its own time (seed).
   */
  private flicker( t: number, seed: number ): number {
    const P = 5 + seed * 0.7, n = Math.floor( t / P ), u = t - n * P;
    const h = ( a: number ) => { const x = Math.sin( a * 127.1 + seed * 311.7 ) * 43758.5453; return x - Math.floor( x ); };
    if ( h( n ) < 0.3 ) { return 0; }
    const q = u - ( 0.4 + h( n + 0.5 ) * ( P - 2.6 ) );
    if ( q < 0 || q > 1.8 ) { return 0; }
    return h( Math.floor( q * 9 ) + n * 13 ) > 0.42 ? 1 : 0;
  }

  /** The traffic along the road, far lane first: cabs, the bus and the cars moving, wrapping unseen. */
  private drawTraffic( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width, F = this.F;
    for ( const c of this.cars ) {
      if ( dt <= 0 ) { continue; }
      c.x += c.dir * c.speed * dt;
      const half = ( W / 2 + 120 ) * c.lane / F;
      if ( c.x > half ) { c.x = -half; } else if ( c.x < -half ) { c.x = half; }
    }
    const order = this.cars.slice().sort( ( a, b ) => b.lane - a.lane );
    for ( const c of order ) { this.vehicle( ctx, c ); }
  }

  /**
   * One vehicle in true perspective, no wider than about 39 degrees off the axis so it keeps its shape at the
   * frame's edge (kaizen: real bodies, not boxes): its side drawn from a profile with its curves, the roof and
   * the end toward us between the two sides, the wheels in their arches, the glass, the lamps, a sheen of the
   * street lights along the wet paint. The cab tall and rounded, black, its TAXI sign lit; the bus red in two
   * decks of lit windows with its blind lit over the windscreen; the cars in London's usual colours.
   */
  private vehicle( ctx: CanvasRenderingContext2D, c: Vehicle ): void {
    const F = this.F, pz = c.lane, k = F / pz;
    const bus = c.kind === 1, cab = c.kind === 0;
    const L = bus ? 8.4 : cab ? 4.6 : 4.4, Wd = bus ? 2.44 : 1.8, hl = L / 2, hw = Wd / 2;
    const lim = 0.8 * pz, pc = Math.max( -lim, Math.min( lim, c.x ) ), slide = F * ( c.x - pc ) / pz;
    const at = ( u: number, v: number, y: number ): [ number, number ] => { const [ sx, sy ] = this.P( pc + c.dir * u, y, pz + v ); return [ sx + slide, sy ]; };
    const fill = ( pts: [ number, number ][], f: string | CanvasGradient ) => { ctx.fillStyle = f; ctx.beginPath(); pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x, y ) : ctx.moveTo( x, y ) ); ctx.closePath(); ctx.fill(); };
    const cols: RGB[] = [ [ 22, 22, 26 ], [ 150, 152, 158 ], [ 30, 50, 90 ], [ 120, 30, 34 ] ];
    const body: RGB = cab ? [ 18, 18, 22 ] : bus ? [ 206, 26, 34 ] : cols[ c.col ];
    const tone = ( kk: number ) => this.rgba( this.mix( body, kk > 0 ? [ 255, 226, 180 ] : [ 0, 0, 0 ], Math.abs( kk ) ), 1 );
    // the side's profile, rear to front, in (u along, y up)
    const prof: [ number, number ][] = bus
      // (owner: the double deckers as they are) a Routemaster's side: its roof rounded at both ends, the front
      // a little raked above the cab
      ? [ [ -hl, 0.32 ], [ -hl, 3.85 ], [ -hl + 0.12, 4.15 ], [ -hl + 0.45, 4.36 ], [ hl - 0.7, 4.36 ], [ hl - 0.25, 4.15 ], [ hl - 0.05, 3.8 ], [ hl, 2.6 ], [ hl, 0.32 ] ]
      : cab
        ? [ [ -hl, 0.36 ], [ -hl, 1.0 ], [ -hl + 0.3, 1.16 ], [ -hl + 0.55, 1.74 ], [ hl - 1.6, 1.8 ], [ hl - 1.05, 1.12 ], [ hl - 0.2, 1.0 ], [ hl, 0.48 ] ]
        : [ [ -hl, 0.34 ], [ -hl, 0.86 ], [ -hl + 0.4, 0.96 ], [ -hl + 1.1, 1.42 ], [ hl - 1.7, 1.45 ], [ hl - 1.0, 0.98 ], [ hl - 0.1, 0.88 ], [ hl, 0.4 ] ];
    // its shadow under it on the wet road
    fill( [ at( -hl, -hw, 0.02 ), at( hl, -hw, 0.02 ), at( hl, hw, 0.02 ), at( -hl, hw, 0.02 ) ], 'rgba(0,0,0,0.4)' );
    // the roof between the two profiles (seen from above for the cars, hidden for the bus)
    if ( !bus ) {
      const top = prof.slice( 2, prof.length - 2 );
      for ( let i = 0; i < top.length - 1; i++ ) {
        const [ u0, y0 ] = top[ i ], [ u1, y1 ] = top[ i + 1 ];
        fill( [ at( u0, -hw * 0.92, y0 ), at( u1, -hw * 0.92, y1 ), at( u1, hw * 0.92, y1 ), at( u0, hw * 0.92, y0 ) ], tone( i === 1 ? 0.22 : 0.08 ) );
      }
    }
    // the end we see: its front or its back by the way it goes
    const seesEnd = ( u: number ) => { const ex = pc + c.dir * u; return ( c.dir * Math.sign( u ) ) * -ex > 0; };
    if ( bus ) {
      // (owner: the bus closed on top, in 3D) its far side, then every edge of its profile carried across to
      // it as a band (the ends, the rounded corners, the roof), so the body is closed all round
      fill( prof.map( ( [ u, y ] ) => at( u, hw, y ) ), tone( -0.4 ) );
      for ( let i = 0; i < prof.length - 1; i++ ) {
        const [ u0, y0 ] = prof[ i ], [ u1, y1 ] = prof[ i + 1 ];
        const up = Math.abs( u1 - u0 ) > Math.abs( y1 - y0 );
        fill( [ at( u0, -hw, y0 ), at( u1, -hw, y1 ), at( u1, hw, y1 ), at( u0, hw, y0 ) ], tone( up ? 0.14 : -0.12 ) );
      }
      // the end toward us: the front with its windscreen, the upper deck's front windows and the route blind,
      // or the back with its upper window and the platform's dark opening
      for ( const u of [ hl, -hl ] ) {
        if ( !seesEnd( u ) ) { continue; }
        const lit = 'rgb(255,226,170)';
        if ( u > 0 ) {
          fill( [ at( u + 0.01, -hw + 0.15, 1.45 ), at( u + 0.01, hw - 0.15, 1.45 ), at( u + 0.01, hw - 0.15, 2.4 ), at( u + 0.01, -hw + 0.15, 2.4 ) ], 'rgb(46,52,64)' );
          for ( const [ v0, v1 ] of [ [ -hw + 0.15, -0.05 ], [ 0.05, hw - 0.15 ] ] ) { fill( [ at( u - 0.04, v0, 2.98 ), at( u - 0.04, v1, 2.98 ), at( u - 0.04, v1, 3.7 ), at( u - 0.04, v0, 3.7 ) ], lit ); }
          fill( [ at( u - 0.04, -hw + 0.3, 3.76 ), at( u - 0.04, hw - 0.3, 3.76 ), at( u - 0.04, hw - 0.3, 4.05 ), at( u - 0.04, -hw + 0.3, 4.05 ) ], 'rgb(250,232,150)' );
          fill( [ at( u + 0.02, -hw + 0.3, 0.45 ), at( u + 0.02, hw - 0.3, 0.45 ), at( u + 0.02, hw - 0.3, 1.25 ), at( u + 0.02, -hw + 0.3, 1.25 ) ], tone( -0.3 ) );
        } else {
          fill( [ at( u - 0.01, -hw + 0.25, 2.98 ), at( u - 0.01, hw - 0.25, 2.98 ), at( u - 0.01, hw - 0.25, 3.7 ), at( u - 0.01, -hw + 0.25, 3.7 ) ], lit );
          fill( [ at( u - 0.01, -hw + 0.1, 0.55 ), at( u - 0.01, hw * 0.2, 0.55 ), at( u - 0.01, hw * 0.2, 2.4 ), at( u - 0.01, -hw + 0.1, 2.4 ) ], 'rgb(36,30,30)' );
        }
      }
    } else {
      for ( const u of [ hl, -hl ] ) {
        if ( !seesEnd( u ) ) { continue; }
        const yTop = u > 0 ? prof[ prof.length - 2 ][ 1 ] : prof[ 1 ][ 1 ];
        fill( [ at( u, -hw, 0.32 ), at( u, hw, 0.32 ), at( u, hw, yTop ), at( u, -hw, yTop ) ], tone( -0.1 ) );
      }
    }
    // the near side from its profile
    const side = prof.map( ( [ u, y ] ) => at( u, -hw, y ) );
    const sg = ctx.createLinearGradient( 0, at( 0, -hw, bus ? 4.4 : 1.8 )[ 1 ], 0, at( 0, -hw, 0.3 )[ 1 ] );
    sg.addColorStop( 0, tone( 0.12 ) );
    sg.addColorStop( 0.55, tone( 0.02 ) );
    sg.addColorStop( 1, tone( -0.3 ) );
    fill( side, sg );
    if ( bus ) {
      const dir = c.dir;
      void dir;
      // the upper deck's windows lit, their pillars, the front pair wrapping the corner
      const win = ( u0: number, u1: number, y0: number, y1: number ) => fill( [ at( u0, -hw - 0.01, y0 ), at( u1, -hw - 0.01, y0 ), at( u1, -hw - 0.01, y1 ), at( u0, -hw - 0.01, y1 ) ], 'rgb(255,226,170)' );
      for ( let q = 0; q < 6; q++ ) { const u0 = -hl + 0.35 + q * ( L - 0.7 ) / 6; win( u0 + 0.08, u0 + ( L - 0.7 ) / 6 - 0.08, 2.98, 3.92 ); }
      // the cream band between the decks
      fill( [ at( -hl, -hw - 0.01, 2.6 ), at( hl, -hw - 0.01, 2.6 ), at( hl, -hw - 0.01, 2.76 ), at( -hl, -hw - 0.01, 2.76 ) ], 'rgb(232,218,180)' );
      // the lower deck: four windows between the open platform at the back and the driver's cab at the front
      for ( let q = 0; q < 4; q++ ) { const u0 = -hl + 1.5 + q * 1.3; win( u0 + 0.08, u0 + 1.22, 1.45, 2.38 ); }
      // the open rear platform, dark inside, its pole, the stair's first treads lit
      fill( [ at( -hl + 0.05, -hw - 0.01, 0.55 ), at( -hl + 1.3, -hw - 0.01, 0.55 ), at( -hl + 1.3, -hw - 0.01, 2.4 ), at( -hl + 0.05, -hw - 0.01, 2.4 ) ], 'rgb(36,30,30)' );
      fill( [ at( -hl + 0.3, -hw - 0.02, 0.6 ), at( -hl + 1.2, -hw - 0.02, 0.6 ), at( -hl + 1.2, -hw - 0.02, 0.8 ), at( -hl + 0.3, -hw - 0.02, 0.8 ) ], 'rgb(200,170,120)' );
      { const [ ax, ay ] = at( -hl + 0.65, -hw - 0.03, 0.55 ), [ , by ] = at( -hl + 0.65, -hw - 0.03, 2.4 ); ctx.strokeStyle = 'rgb(210,206,196)'; ctx.lineWidth = Math.max( 0.6, k * 0.04 ); ctx.beginPath(); ctx.moveTo( ax, ay ); ctx.lineTo( ax, by ); ctx.stroke(); }
      // the driver's cab: its own window over the bonnet, the bonnet and its grille at the front
      win( hl - 1.25, hl - 0.15, 1.5, 2.4 );
      fill( [ at( hl - 1.3, -hw - 0.01, 0.4 ), at( hl, -hw - 0.01, 0.4 ), at( hl, -hw - 0.01, 1.35 ), at( hl - 1.3, -hw - 0.01, 1.35 ) ], tone( -0.15 ) );
      // the route blind over the cab and its number, the roundel on the side
      fill( [ at( hl - 1.6, -hw - 0.02, 3.98 ), at( hl - 0.3, -hw - 0.02, 3.98 ), at( hl - 0.3, -hw - 0.02, 4.22 ), at( hl - 1.6, -hw - 0.02, 4.22 ) ], 'rgb(250,232,150)' );
      { const [ rx, ry ] = at( 0.4, -hw - 0.02, 2.1 ); ctx.strokeStyle = 'rgb(236,226,200)'; ctx.lineWidth = Math.max( 0.6, k * 0.05 ); ctx.beginPath(); ctx.arc( rx, ry, k * 0.18, 0, Math.PI * 2 ); ctx.stroke(); }
      // the skirt along the foot
      fill( [ at( -hl, -hw - 0.01, 0.32 ), at( hl, -hw - 0.01, 0.32 ), at( hl, -hw - 0.01, 0.55 ), at( -hl, -hw - 0.01, 0.55 ) ], this.rgba( this.mix( body, [ 0, 0, 0 ], 0.45 ), 1 ) );
    } else {
      // the cabin's side glass in two panes and the pillar between
      const gy0 = cab ? 1.12 : 0.98, gy1 = ( cab ? 1.74 : 1.4 ) - 0.06;
      const gu0 = cab ? -hl + 0.65 : -hl + 1.2, gu1 = cab ? hl - 1.2 : hl - 1.15;
      fill( [ at( gu0, -hw - 0.01, gy0 ), at( gu1, -hw - 0.01, gy0 ), at( gu1 - 0.4, -hw - 0.01, gy1 ), at( gu0 + 0.15, -hw - 0.01, gy1 ) ], cab ? 'rgb(84,76,66)' : 'rgb(58,66,80)' );
      const um = ( gu0 + gu1 ) / 2;
      fill( [ at( um - 0.06, -hw - 0.02, gy0 ), at( um + 0.06, -hw - 0.02, gy0 ), at( um + 0.06, -hw - 0.02, gy1 ), at( um - 0.06, -hw - 0.02, gy1 ) ], tone( 0 ) );
      if ( cab ) {
        const [ tx, ty ] = at( hl - 1.75, 0, 1.92 );
        ctx.fillStyle = 'rgb(255,200,70)';
        ctx.fillRect( tx - k * 0.3, ty - k * 0.08, k * 0.6, k * 0.16 );
      }
    }
    // the sheen of the street lights along the shoulder
    const beltY = bus ? 1.15 : cab ? 1.02 : 0.9;
    ctx.strokeStyle = 'rgba(255,230,190,0.35)';
    ctx.lineWidth = Math.max( 0.6, k * 0.03 );
    { const [ ax, ay ] = at( -hl + 0.3, -hw - 0.02, beltY ), [ bx, by ] = at( hl - 0.3, -hw - 0.02, beltY ); ctx.beginPath(); ctx.moveTo( ax, ay ); ctx.lineTo( bx, by ); ctx.stroke(); }
    // the wheels in their dark arches
    const wr = bus ? 0.52 : 0.36;
    for ( const u of bus ? [ -hl + 2.0, hl - 1.7 ] : [ -hl + 0.95, hl - 0.95 ] ) {
      const [ ax, ay ] = at( u, -hw - 0.01, wr );
      ctx.fillStyle = 'rgba(0,0,0,0.75)';
      ctx.beginPath(); ctx.ellipse( ax, ay, k * wr * 1.15, k * wr * 1.15, 0, Math.PI, 0 ); ctx.fill();
      ctx.fillStyle = 'rgb(14,14,16)';
      ctx.beginPath(); ctx.ellipse( ax, ay, k * wr, k * wr * 0.98, 0, 0, Math.PI * 2 ); ctx.fill();
      ctx.fillStyle = 'rgb(130,132,138)';
      ctx.beginPath(); ctx.ellipse( ax, ay, k * wr * 0.45, k * wr * 0.44, 0, 0, Math.PI * 2 ); ctx.fill();
    }
    // (owner: the lamps drawn in detail) each kind's own lamps on the end we see: the cab's round headlamps
    // and tall tail lamps, the car's swept headlamps and its strip of tails, the bus's round lamps in their
    // chrome rims with the sidelights over them; each lamp a dark housing, its lens and a hot core, a halo in
    // the wet air and its streak on the wet road below; the corner lenses on the near side; the headlamps'
    // beam on the road ahead
    const head: RGB = [ 255, 242, 216 ], tail: RGB = [ 255, 44, 38 ], amber: RGB = [ 255, 168, 60 ];
    const [ , groundY ] = at( 0, -hw, 0 );
    const sprite = ( col: RGB, x: number, y: number, rx: number, ry: number, a: number ) => {
      if ( rx < 0.3 || ry < 0.3 ) { return; }
      ctx.globalAlpha = Math.min( 1, a );
      ctx.drawImage( this.glowSprite( col ), x - rx, y - ry, rx * 2, ry * 2 );
    };
    // the beam ahead, low on the wet road
    {
      const p0 = at( hl, -hw + 0.25, 0.02 ), p1 = at( hl + 7, -hw - 0.7, 0.02 ), p2 = at( hl + 7, hw + 0.7, 0.02 ), p3 = at( hl, hw - 0.25, 0.02 );
      const bg = ctx.createLinearGradient( p0[ 0 ], 0, p1[ 0 ], 0 );
      bg.addColorStop( 0, this.rgba( head, 0.16 ) );
      bg.addColorStop( 1, this.rgba( head, 0 ) );
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      fill( [ p0, p1, p2, p3 ], bg );
      ctx.restore();
    }
    // each lamp as (u, v across, y up, half width, half height, round?, colour, how bright)
    type Lamp = [ number, number, number, number, number, boolean, RGB, number ];
    const lamps: Lamp[] = [];
    for ( const sv of [ -1, 1 ] ) {
      if ( bus ) {
        lamps.push( [ hl, sv * ( hw - 0.32 ), 0.86, 0.14, 0.14, true, head, 1 ], [ hl, sv * ( hw - 0.32 ), 1.18, 0.06, 0.05, true, [ 255, 236, 200 ], 0.6 ] );
        lamps.push( [ -hl, sv * ( hw - 0.16 ), 0.78, 0.09, 0.09, true, tail, 0.9 ], [ -hl, sv * ( hw - 0.16 ), 1.0, 0.06, 0.05, true, amber, 0.4 ] );
      } else if ( cab ) {
        lamps.push( [ hl, sv * ( hw - 0.3 ), 0.74, 0.12, 0.12, true, head, 1 ] );
        lamps.push( [ -hl, sv * ( hw - 0.1 ), 0.8, 0.06, 0.17, false, tail, 0.9 ] );
      } else {
        lamps.push( [ hl, sv * ( hw - 0.3 ), 0.7, 0.2, 0.07, false, head, 1 ] );
        lamps.push( [ -hl, sv * ( hw - 0.26 ), 0.8, 0.22, 0.06, false, tail, 0.9 ] );
      }
    }
    for ( const [ u, v, y, a, b, round, col, br ] of lamps ) {
      if ( !seesEnd( u ) ) { continue; }
      const e = u + Math.sign( u ) * 0.015, [ cx, cy ] = at( e, v, y );
      const rx = Math.max( 0.5, Math.abs( at( e, v + a, y )[ 0 ] - at( e, v - a, y )[ 0 ] ) / 2 ), ry = Math.max( 0.5, k * b );
      // the housing, the lens, the core
      ctx.globalAlpha = 1;
      ctx.fillStyle = bus && round && col === head ? 'rgb(176,178,184)' : 'rgb(24,24,28)';
      ctx.beginPath();
      if ( round ) { ctx.ellipse( cx, cy, rx * 1.3 + 0.3, ry * 1.3 + 0.3, 0, 0, Math.PI * 2 ); } else { ctx.rect( cx - rx - 0.4, cy - ry - 0.4, rx * 2 + 0.8, ry * 2 + 0.8 ); }
      ctx.fill();
      ctx.fillStyle = this.rgba( this.mix( col, [ 255, 255, 255 ], col === head ? 0.2 : 0.12 ), 1 );
      ctx.beginPath();
      if ( round ) { ctx.ellipse( cx, cy, rx, ry, 0, 0, Math.PI * 2 ); } else { ctx.rect( cx - rx, cy - ry, rx * 2, ry * 2 ); }
      ctx.fill();
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      sprite( [ 255, 255, 255 ], cx, cy, rx * 0.8, ry * 0.8, 0.7 * br );
      // its halo in the rain and its streak on the wet road below the car
      sprite( col, cx, cy, k * 0.55, k * 0.55, 0.55 * br );
      sprite( col, cx, cy, k * 1.5, k * 1.2, 0.12 * br );
      const drop = groundY - cy;
      sprite( col, cx, groundY + drop * 1.2 + k * 0.3, Math.max( 0.8, rx * 1.1 ), drop * 1.4 + k * 0.5, 0.3 * br );
      ctx.restore();
    }
    // the corner lenses on the near side, a clear one at the front and a red one at the back
    for ( const [ u0, u1, y0, y1, col ] of ( bus
      ? [ [ hl - 0.12, hl, 0.72, 1.0, head ], [ -hl, -hl + 0.1, 0.68, 0.9, tail ] ]
      : cab ? [ [ hl - 0.16, hl, 0.66, 0.84, head ], [ -hl, -hl + 0.08, 0.64, 0.98, tail ] ]
        : [ [ hl - 0.3, hl - 0.02, 0.64, 0.76, head ], [ -hl + 0.02, -hl + 0.3, 0.74, 0.86, tail ] ] ) as [ number, number, number, number, RGB ][] ) {
      ctx.globalAlpha = 1;
      fill( [ at( u0, -hw - 0.02, y0 ), at( u1, -hw - 0.02, y0 ), at( u1, -hw - 0.02, y1 ), at( u0, -hw - 0.02, y1 ) ], this.rgba( this.mix( col, [ 255, 255, 255 ], 0.15 ), 1 ) );
      const [ mx, my ] = at( ( u0 + u1 ) / 2, -hw - 0.02, ( y0 + y1 ) / 2 );
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      sprite( col, mx, my, k * 0.35, k * 0.3, 0.4 );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /** A soft round glow in one colour, made once and kept: its heart bright, fading out to nothing. */
  private glowSprites = new Map<string, HTMLCanvasElement>();
  private glowSprite( col: RGB ): HTMLCanvasElement {
    const key = col.join( ',' );
    let c = this.glowSprites.get( key );
    if ( !c ) {
      c = document.createElement( 'canvas' );
      c.width = c.height = 64;
      const g = c.getContext( '2d' )!, gr = g.createRadialGradient( 32, 32, 0, 32, 32, 32 );
      gr.addColorStop( 0, this.rgba( col, 1 ) );
      gr.addColorStop( 0.25, this.rgba( col, 0.5 ) );
      gr.addColorStop( 0.6, this.rgba( col, 0.12 ) );
      gr.addColorStop( 1, this.rgba( col, 0 ) );
      g.fillStyle = gr;
      g.fillRect( 0, 0, 64, 64 );
      this.glowSprites.set( key, c );
    }
    return c;
  }

  /** The rain's tone: the pale of the circle, cooled by the steel blue. */
  private rainTone(): RGB {
    const [ , , , steel, pale ] = this.five();
    return this.mix( pale, steel, 0.35 );
  }

  /** (owner) the rain: a drizzle at rest, a downpour on the charge, each drop a lit streak slanting a little. */
  private drawRain( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width, H = this.height, e = this.energy, slant = 0.14;
    // (owner: more rain falling at rest)
    const share = 0.3 + 0.7 * e;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = this.rgba( this.rainTone(), 1 );
    ctx.lineWidth = Math.max( 0.7, 1.05 * this.scaleK );
    ctx.lineCap = 'round';
    for ( const d of this.drops ) {
      const v = d.speed * ( 1 + 0.5 * e );
      d.y += v * dt;
      d.x += v * slant * dt;
      if ( d.y - d.len > H ) { d.y = -d.len - Math.random() * H * 0.2; d.x = Math.random() * ( W + H * slant ) - H * slant; }
      if ( d.x > W + 20 ) { d.x -= W + H * slant; }
      if ( d.rank > share ) { continue; }
      ctx.globalAlpha = d.alpha * ( 0.6 + 0.6 * e );
      ctx.beginPath();
      ctx.moveTo( d.x, d.y );
      ctx.lineTo( d.x - d.len * slant, d.y - d.len );
      ctx.stroke();
    }
    ctx.restore();
  }

  /** (owner) the wallpaper: the rain, still, all down the page. */
  private drawWallRain( ctx: CanvasRenderingContext2D ): void {
    const W = this.width, H = this.height, r = this.seededLocal( 77 ), k = this.scaleK, slant = 0.14;
    ctx.save();
    ctx.strokeStyle = this.rgba( this.rainTone(), 1 );
    ctx.lineWidth = Math.max( 0.8, 1.1 * k );
    ctx.lineCap = 'round';
    const n = Math.round( W * H / 8000 );
    for ( let i = 0; i < n; i++ ) {
      const x = r() * W, y = r() * H, len = ( 14 + r() * 22 ) * k;
      ctx.globalAlpha = 0.14 + r() * 0.22;
      ctx.beginPath(); ctx.moveTo( x, y ); ctx.lineTo( x - len * slant, y - len ); ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * The still scene, painted once: the night sky; the palace's front and the tower floodlit; the road, its
   * far railings and bare trees; then on the front layer the pavement shining wet, the bollards, the lamps and
   * the telephone box.
   */
  private bake( scale: number, lit = false ): void {
    // (owner: the buildings' lights come on with the charge, most of them off at rest) the same draws, a
    // window lit at rest is lit in the bright copy too
    const onK = lit ? 0.8 : 0.18;
    const W = this.width, H = this.height, F = this.F, hz = this.hz;
    // (owner: the tower 50 px to the left on screen, then 50 more)
    const TX = TOWER.x - 100 * TOWER.z / F;
    const [ slate, red, pink, steel, pale ] = this.five();
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g2 = c.getContext( '2d' )!;
      g2.scale( scale, scale );
      return { c, g: g2 };
    };
    let g: CanvasRenderingContext2D;
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    const rr = ( seed: number ) => this.seededLocal( seed );
    const rgb = ( c: RGB, a = 1 ) => this.rgba( c, a );
    const night: RGB = this.mix( slate, [ 14, 14, 26 ], 0.6 );
    const gold: RGB = [ 236, 176, 84 ], goldHi: RGB = [ 255, 222, 150 ], goldLo: RGB = [ 150, 96, 40 ];
    const warmWin: RGB = [ 255, 214, 140 ];
    const glow = ( x: number, y: number, rad: number, col: RGB, a: number ) => {
      const gr = g.createRadialGradient( x, y, 0, x, y, rad );
      gr.addColorStop( 0, rgb( col, a ) );
      gr.addColorStop( 0.35, rgb( col, a * 0.35 ) );
      gr.addColorStop( 1, rgb( col, 0 ) );
      g.fillStyle = gr;
      g.fillRect( x - rad, y - rad, rad * 2, rad * 2 );
    };

    const bk = canvas();
    g = bk.g;
    // ---- the night sky: deep slate indigo, the city's glow low on it, a few stars ----
    {
      const sg = g.createLinearGradient( 0, 0, 0, hz );
      sg.addColorStop( 0, rgb( this.mix( night, [ 6, 6, 16 ], 0.4 ) ) );
      sg.addColorStop( 0.7, rgb( this.mix( night, steel, 0.12 ) ) );
      sg.addColorStop( 1, rgb( this.mix( this.mix( night, [ 120, 90, 70 ], 0.35 ), pink, 0.06 ) ) );
      g.fillStyle = sg;
      g.fillRect( 0, 0, W, hz + 2 );
      const r = rr( 3 );
      for ( let q = 0; q < W * H / 9000; q++ ) {
        g.fillStyle = rgb( pale, 0.25 + r() * 0.4 );
        g.beginPath(); g.arc( r() * W, r() * hz * 0.8, 0.5 + r() * 0.8, 0, Math.PI * 2 ); g.fill();
      }
    }
    // ---- the ground beyond the road: the square's paving and dark lawns to the buildings ----
    {
      const [ , y0 ] = P( 0, 0, 900 ), [ , y1 ] = P( 0, 0, ROAD.far );
      const gg = g.createLinearGradient( 0, y0, 0, y1 );
      gg.addColorStop( 0, rgb( this.mix( night, [ 70, 60, 46 ], 0.3 ) ) );
      gg.addColorStop( 1, rgb( this.mix( night, [ 30, 38, 34 ], 0.45 ) ) );
      g.fillStyle = gg;
      g.fillRect( 0, y0, W, y1 - y0 + 1 );
    }
    // a building front facing us from x0 to x1 at depth z, h high, in one of London's dresses: Portland stone
    // (0), red brick banded in stone with gables and turrets (1), stone under a slate mansard (2); lit warm
    // from the street below, its windows lit or dark, a cornice, its roof; its end toward the middle shown
    // (owner: the buildings' drawing order) the ends are drawn in a pass of their own before the fronts, so a
    // neighbour standing back or lower covers just the part of an end that lies behind it
    const endOf = ( x0: number, x1: number, z: number, h: number, style: number, depth = 16 ) => {
      const base: RGB = style === 1 ? [ 150, 72, 58 ] : style === 2 ? [ 176, 166, 150 ] : [ 196, 188, 170 ];
      const ex = ( x0 + x1 ) / 2 < 0 ? x1 : x0;
      // (kaizen: an end standing free read as a dark slab) the side wall lit from the street like the front,
      // a shade darker, its windows along its depth, a few lit
      const side = ( k: number ) => this.mix( this.mix( base, night, 0.72 ), this.mix( base, [ 255, 214, 160 ], 0.1 ), k );
      const eg = g.createLinearGradient( 0, P( ex, h, z )[ 1 ], 0, P( ex, 0, z )[ 1 ] );
      eg.addColorStop( 0, rgb( side( 0 ) ) );
      eg.addColorStop( 0.7, rgb( side( 0.22 ) ) );
      eg.addColorStop( 1, rgb( side( 0.55 ) ) );
      poly( [ P( ex, 0, z ), P( ex, 0, z + depth ), P( ex, h, z + depth ), P( ex, h, z ) ], eg );
      const r = rr( Math.round( Math.abs( ex ) * 13 ) + 77 );
      for ( let y = 5.2; y < h - 3.2; y += 3.4 ) {
        for ( let d = 1.6; d < depth - 1.5; d += 3.1 ) {
          const wc = r() < onK ? this.mix( warmWin, night, 0.3 ) : this.mix( [ 34, 40, 56 ], night, 0.5 );
          poly( [ P( ex, y, z + d ), P( ex, y, z + d + 1.3 ), P( ex, y + 2.1, z + d + 1.3 ), P( ex, y + 2.1, z + d ) ], rgb( wc ) );
        }
      }
    };
    const front = ( x0: number, x1: number, z: number, h: number, style: number, r: () => number ) => {
      const s = F / z;
      const base: RGB = style === 1 ? [ 150, 72, 58 ] : style === 2 ? [ 176, 166, 150 ] : [ 196, 188, 170 ];
      const lit = ( k: number ) => this.mix( this.mix( base, night, 0.55 ), this.mix( base, [ 255, 214, 160 ], 0.25 ), k );
      const fg = g.createLinearGradient( 0, P( 0, h, z )[ 1 ], 0, P( 0, 0, z )[ 1 ] );
      fg.addColorStop( 0, rgb( lit( 0.05 ) ) );
      fg.addColorStop( 0.7, rgb( lit( 0.3 ) ) );
      fg.addColorStop( 1, rgb( lit( 0.75 ) ) );
      poly( [ P( x0, 0, z ), P( x1, 0, z ), P( x1, h, z ), P( x0, h, z ) ], fg );
      // the rusticated ground floor and its lit doorways
      g.strokeStyle = rgb( this.mix( base, night, 0.7 ), 0.6 );
      g.lineWidth = Math.max( 0.4, s * 0.04 );
      for ( let y = 0.6; y < 4.4; y += 0.6 ) { const [ ax, ay ] = P( x0, y, z - 0.02 ), [ bx ] = P( x1, y, z - 0.02 ); g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, ay ); g.stroke(); }
      // the floors: windows in bays, string courses between, the stone bands of the brick ones
      const bay = 3.1, nb = Math.max( 2, Math.floor( ( x1 - x0 - 1 ) / bay ) ), pad = ( x1 - x0 - nb * bay ) / 2;
      for ( let y = 5.2; y < h - 3.2; y += 3.4 ) {
        if ( style === 1 || style === 0 ) { poly( [ P( x0, y - 0.45, z - 0.02 ), P( x1, y - 0.45, z - 0.02 ), P( x1, y - 0.3, z - 0.02 ), P( x0, y - 0.3, z - 0.02 ) ], rgb( style === 1 ? this.mix( [ 220, 212, 196 ], night, 0.5 ) : lit( 0.45 ), 0.8 ) ); }
        for ( let q = 0; q < nb; q++ ) {
          const wx = x0 + pad + q * bay + bay * 0.28, ww = bay * 0.44;
          const on = r() < onK;
          const wc = on ? this.mix( warmWin, [ 255, 240, 200 ], r() * 0.5 ) : this.mix( [ 40, 50, 70 ], night, 0.4 );
          poly( [ P( wx, y, z - 0.03 ), P( wx + ww, y, z - 0.03 ), P( wx + ww, y + 2.1, z - 0.03 ), P( wx, y + 2.1, z - 0.03 ) ], rgb( wc ) );
          if ( s * ww > 4 ) {
            g.strokeStyle = rgb( this.mix( base, night, 0.4 ), 0.9 );
            g.lineWidth = Math.max( 0.4, s * 0.06 );
            const [ mx, my ] = P( wx + ww / 2, y, z - 0.04 ), [ , my2 ] = P( wx + ww / 2, y + 2.1, z - 0.04 ), [ hx0, hy ] = P( wx, y + 1.05, z - 0.04 ), [ hx1 ] = P( wx + ww, y + 1.05, z - 0.04 );
            g.beginPath(); g.moveTo( mx, my ); g.lineTo( mx, my2 ); g.moveTo( hx0, hy ); g.lineTo( hx1, hy ); g.stroke();
          }
        }
      }
      // the cornice and its shadow
      poly( [ P( x0 - 0.3, h - 0.9, z - 0.3 ), P( x1 + 0.3, h - 0.9, z - 0.3 ), P( x1 + 0.3, h - 0.3, z - 0.3 ), P( x0 - 0.3, h - 0.3, z - 0.3 ) ], rgb( lit( 0.35 ) ) );
      poly( [ P( x0, h - 1.3, z - 0.02 ), P( x1, h - 1.3, z - 0.02 ), P( x1, h - 0.9, z - 0.02 ), P( x0, h - 0.9, z - 0.02 ) ], rgb( this.mix( base, night, 0.8 ) ) );
      const roofC = this.mix( [ 70, 74, 86 ], night, 0.45 );
      if ( style === 0 ) {
        // a balustrade on the parapet, chimney stacks behind
        for ( let x = x0; x < x1; x += 0.7 ) { poly( [ P( x, h - 0.3, z - 0.2 ), P( x + 0.3, h - 0.3, z - 0.2 ), P( x + 0.3, h + 0.6, z - 0.2 ), P( x, h + 0.6, z - 0.2 ) ], rgb( lit( 0.2 ) ) ); }
        poly( [ P( x0, h + 0.6, z - 0.2 ), P( x1, h + 0.6, z - 0.2 ), P( x1, h + 0.8, z - 0.2 ), P( x0, h + 0.8, z - 0.2 ) ], rgb( lit( 0.25 ) ) );
        for ( let x = x0 + 3; x < x1 - 2; x += 7 + r() * 5 ) { poly( [ P( x, h, z + 6 ), P( x + 1.6, h, z + 6 ), P( x + 1.6, h + 3.5, z + 6 ), P( x, h + 3.5, z + 6 ) ], rgb( this.mix( base, night, 0.65 ) ) ); }
      } else if ( style === 1 ) {
        // (owner: the brown buildings' tops were broken) red brick "Pont Street Dutch" done in order: the slate
        // roof behind first, then the curved Dutch gables standing on the front between stone copings, each with
        // its small arched window, spaced evenly and kept inside the front, clear of the corner turret; the
        // turret last, inside the front's corner toward the middle, banded in stone under its pointed slate cap
        const stone = this.mix( [ 220, 212, 196 ], night, 0.45 ), inner = ( x0 + x1 ) / 2 < 0;
        const tw = 3.2, tx0 = inner ? x1 - tw - 0.2 : x0 + 0.2, tx1 = tx0 + tw;
        poly( [ P( x0, h, z ), P( x1, h, z ), P( x1, h + 2.4, z + 3.2 ), P( x0, h + 2.4, z + 3.2 ) ], rgb( roofC ) );
        const f0 = inner ? x0 + 0.8 : tx1 + 0.8, f1 = inner ? tx0 - 0.8 : x1 - 0.8, gw = 5;
        const ng = Math.max( 0, Math.floor( ( f1 - f0 + 1.2 ) / ( gw + 1.2 ) ) ), gap = ng > 0 ? ( f1 - f0 - ng * gw ) / ( ng + 1 ) : 0;
        for ( let q = 0; q < ng; q++ ) {
          const ga = f0 + gap + q * ( gw + gap ), gb = ga + gw, gm = ( ga + gb ) / 2, zf = z - 0.05;
          // the outline: upright sides, concave shoulders sweeping in, the narrow top under a small pediment
          const half: [ number, number ][] = [ [ ga, h - 0.05 ], [ ga, h + 1.5 ] ];
          for ( let k = 1; k <= 7; k++ ) { const t = k / 7 * Math.PI / 2; half.push( [ ga + 1.5 * Math.sin( t ), h + 2.9 - 1.4 * Math.cos( t ) ] ); }
          half.push( [ ga + 1.5, h + 3.9 ], [ ga + 1.3, h + 3.9 ] );
          const outline = half.concat( [ [ gm, h + 4.7 ] ], half.slice().reverse().map( ( [ x, y ] ): [ number, number ] => [ gb - ( x - ga ), y ] ) );
          const pts = outline.map( ( [ x, y ] ) => P( x, y, zf ) );
          const gg = g.createLinearGradient( 0, P( 0, h + 4.7, z )[ 1 ], 0, P( 0, h, z )[ 1 ] );
          gg.addColorStop( 0, rgb( lit( 0.02 ) ) );
          gg.addColorStop( 1, rgb( lit( 0.12 ) ) );
          poly( pts, gg );
          g.strokeStyle = rgb( stone, 0.85 );
          g.lineWidth = Math.max( 0.5, s * 0.16 );
          g.lineJoin = 'round';
          g.beginPath(); pts.slice( 1, pts.length - 1 ).forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.stroke();
          // its arched window
          const wc = r() < onK ? warmWin : this.mix( [ 40, 50, 70 ], night, 0.3 ), [ wx0, wy0 ] = P( gm - 0.5, h + 0.9, zf - 0.01 ), [ wx1, wy1 ] = P( gm + 0.5, h + 2.5, zf - 0.01 );
          g.fillStyle = rgb( wc );
          g.beginPath(); g.moveTo( wx0, wy0 ); g.lineTo( wx0, wy1 + ( wx1 - wx0 ) / 2 ); g.arc( ( wx0 + wx1 ) / 2, wy1 + ( wx1 - wx0 ) / 2, ( wx1 - wx0 ) / 2, Math.PI, 0 ); g.lineTo( wx1, wy0 ); g.closePath(); g.fill();
        }
        // the corner turret: its shaft in brick with stone bands, its windows, the pointed slate cap
        {
          const zt = z - 0.4, tm = ( tx0 + tx1 ) / 2, tb = h - 5, tt = h + 2.2;
          const tg = g.createLinearGradient( 0, P( 0, tt, zt )[ 1 ], 0, P( 0, tb, zt )[ 1 ] );
          tg.addColorStop( 0, rgb( lit( 0.06 ) ) );
          tg.addColorStop( 1, rgb( lit( 0.32 ) ) );
          poly( [ P( tx0, tb, zt ), P( tx1, tb, zt ), P( tx1, tt, zt ), P( tx0, tt, zt ) ], tg );
          for ( const yb of [ tb, h - 1.4, tt - 0.4 ] ) { poly( [ P( tx0 - 0.1, yb, zt - 0.02 ), P( tx1 + 0.1, yb, zt - 0.02 ), P( tx1 + 0.1, yb + 0.35, zt - 0.02 ), P( tx0 - 0.1, yb + 0.35, zt - 0.02 ) ], rgb( stone ) ); }
          for ( const wy of [ tb + 1.0, h - 0.8 ] ) {
            poly( [ P( tm - 0.45, wy, zt - 0.03 ), P( tm + 0.45, wy, zt - 0.03 ), P( tm + 0.45, wy + 1.6, zt - 0.03 ), P( tm - 0.45, wy + 1.6, zt - 0.03 ) ], rgb( r() < onK ? warmWin : this.mix( [ 40, 50, 70 ], night, 0.3 ) ) );
          }
          poly( [ P( tx0 - 0.25, tt, zt ), P( tx1 + 0.25, tt, zt ), P( tm, tt + 4.4, zt ) ], rgb( roofC ) );
          poly( [ P( tm, tt, zt ), P( tx1 + 0.25, tt, zt ), P( tm, tt + 4.4, zt ) ], rgb( this.mix( roofC, [ 0, 0, 0 ], 0.25 ) ) );
          g.strokeStyle = rgb( this.mix( stone, night, 0.2 ) );
          g.lineWidth = Math.max( 0.5, s * 0.12 );
          const [ fx, fy ] = P( tm, tt + 4.4, zt ), [ , fy2 ] = P( tm, tt + 5.6, zt );
          g.beginPath(); g.moveTo( fx, fy ); g.lineTo( fx, fy2 ); g.stroke();
        }
      } else {
        // the mansard, its dormers lit and dark
        poly( [ P( x0, h, z ), P( x1, h, z ), P( x1, h + 4, z + 2.5 ), P( x0, h + 4, z + 2.5 ) ], rgb( roofC ) );
        for ( let x = x0 + 1.4; x < x1 - 1.4; x += 3.1 ) {
          poly( [ P( x, h + 0.8, z + 0.6 ), P( x + 1.2, h + 0.8, z + 0.6 ), P( x + 1.2, h + 2.3, z + 0.6 ), P( x + 0.6, h + 2.9, z + 0.6 ), P( x, h + 2.3, z + 0.6 ) ], rgb( lit( 0.15 ) ) );
          poly( [ P( x + 0.25, h + 1.0, z + 0.58 ), P( x + 0.95, h + 1.0, z + 0.58 ), P( x + 0.95, h + 2.1, z + 0.58 ), P( x + 0.25, h + 2.1, z + 0.58 ) ], rgb( r() < onK * 0.8 ? warmWin : [ 34, 40, 56 ] ) );
        }
        for ( let x = x0 + 4; x < x1 - 2; x += 9 ) { poly( [ P( x, h + 3, z + 4 ), P( x + 1.4, h + 3, z + 4 ), P( x + 1.4, h + 6, z + 4 ), P( x, h + 6, z + 4 ) ], rgb( this.mix( base, night, 0.6 ) ) ); }
      }
    };
    // ---- the far roofs of Westminster and Whitehall beyond, dim, a few lit windows ----
    {
      const r = rr( 31 );
      // (owner: the right side as the left) the far roofs run on behind the right side too
      for ( let x = -W * 700 / F * 0.6; x < W * 700 / F * 0.6; ) {
        const w = 20 + r() * 30, h = 22 + r() * 26, z = 650 + r() * 150, s = F / z;
        poly( [ P( x, 0, z ), P( x + w, 0, z ), P( x + w, h, z ), P( x, h, z ) ], rgb( this.mix( night, [ 90, 84, 80 ], 0.22 + r() * 0.1 ) ) );
        for ( let q = 0; q < w * h / 30; q++ ) {
          if ( r() > onK * 0.85 ) { continue; }
          const [ wx, wy ] = P( x + r() * w, r() * h, z - 0.1 );
          g.fillStyle = rgb( warmWin, 0.7 );
          g.fillRect( wx, wy, Math.max( 1, s * 1.2 ), Math.max( 1, s * 1.6 ) );
        }
        x += w;
      }
    }
    // ---- the Palace of Westminster: its long Gothic front running to the right of the tower, floodlit ----
    {
      // (owner: the palace ends, other buildings follow on a wide screen) its real length, about 160 m here
      // (owner: the building after the tower on the layer under it, joined to it) the palace runs on from
      // behind the tower: it starts under the tower's middle on screen, the tower drawn over it
      const z = PALACE.z, x0 = TX * z / TOWER.z, x1 = x0 + PALACE.len, h = 26;
      const s = F / z, r = rr( 7 );
      // (owner: the palace and Big Ben starting from the same foot) it comes down to the tower's foot, which
      // stands on the row's line
      const dyP = F * this.eye * ( 1 / 100 - 1 / z );
      const P = ( x: number, y: number, zz: number ): [ number, number ] => { const [ sx, sy ] = this.P( x, y, zz ); return [ sx, sy + dyP ]; };
      // (owner: the tower at Big Ben's right removed)
      // the front's face: gold lit from below, darker up toward the roof
      const fg = g.createLinearGradient( 0, P( 0, h, z )[ 1 ], 0, P( 0, 0, z )[ 1 ] );
      fg.addColorStop( 0, rgb( this.mix( goldLo, night, 0.25 ) ) );
      fg.addColorStop( 0.6, rgb( this.mix( gold, goldLo, 0.35 ) ) );
      fg.addColorStop( 1, rgb( this.mix( gold, goldHi, 0.2 ) ) );
      poly( [ P( x0, 0, z ), P( x1, 0, z ), P( x1, h, z ), P( x0, h, z ) ], fg );
      // its bays: buttresses with pinnacles over the roof line, two floors of lit windows between them
      // (owner: the lights ran past its right end) whole bays only, a last buttress closing the front
      for ( let x = x0; x <= x1 + 0.01; x += 4.2 ) {
        if ( x + 4.2 > x1 + 0.01 ) {
          const xe = x1 - 0.5;
          poly( [ P( xe, 0, z - 0.1 ), P( x1, 0, z - 0.1 ), P( x1, h + 1, z - 0.1 ), P( xe, h + 1, z - 0.1 ) ], rgb( this.mix( goldHi, gold, 0.3 ) ) );
          poly( [ P( xe - 0.3, h + 1, z - 0.1 ), P( x1 + 0.3, h + 1, z - 0.1 ), P( xe + 0.25, h + 6, z - 0.1 ) ], rgb( this.mix( goldLo, gold, 0.4 ) ) );
          break;
        }
        poly( [ P( x, 0, z - 0.1 ), P( x + 0.5, 0, z - 0.1 ), P( x + 0.5, h + 1, z - 0.1 ), P( x, h + 1, z - 0.1 ) ], rgb( this.mix( goldHi, gold, 0.3 ) ) );
        poly( [ P( x - 0.3, h + 1, z - 0.1 ), P( x + 0.8, h + 1, z - 0.1 ), P( x + 0.25, h + 5 + r() * 2, z - 0.1 ) ], rgb( this.mix( goldLo, gold, 0.4 ) ) );
        if ( s * 4 > 6 ) {
          for ( const y of [ 4, 11, 18 ] ) {
            const lit = r() < 0.75;
            poly( [ P( x + 1.3, y, z - 0.05 ), P( x + 3.1, y, z - 0.05 ), P( x + 3.1, y + 3.4, z - 0.05 ), P( x + 2.2, y + 4, z - 0.05 ), P( x + 1.3, y + 3.4, z - 0.05 ) ], rgb( lit ? this.mix( warmWin, [ 255, 245, 210 ], r() * 0.4 ) : this.mix( goldLo, night, 0.5 ) ) );
            g.strokeStyle = rgb( this.mix( goldLo, night, 0.2 ), 0.8 );
            g.lineWidth = Math.max( 0.4, s * 0.12 );
            const [ mx, my0 ] = P( x + 2.2, y, z - 0.06 ), [ , my1 ] = P( x + 2.2, y + 3.6, z - 0.06 );
            g.beginPath(); g.moveTo( mx, my0 ); g.lineTo( mx, my1 ); g.stroke();
          }
        }
      }
      // the roof's dark line and its cresting
      poly( [ P( x0, h, z + 2 ), P( x1, h, z + 2 ), P( x1, h + 3, z + 6 ), P( x0, h + 3, z + 6 ) ], rgb( this.mix( night, steel, 0.15 ) ) );
    }
    // ---- the Elizabeth Tower (owner: Big Ben done well), floodlit gold from below, its left face in shade:
    // the plinth; the shaft between corner buttresses in three bays a face, each storey a pair of lancets in a
    // pointed panel under a string course, a quatrefoil frieze every other storey; the pierced parapet; the
    // clock stage standing out, its dial of opal glass in a gilt square frame with traceried spandrels, its
    // corner pinnacles; the gallery; the belfry's three lancets lit yellow (owner); the spire with its gilt ribs and two
    // tiers of lucarnes, the orb and cross ----
    {
      const { z: tz, w } = TOWER, tx = TX, s = F / tz, half = w / 2;
      // (owner: its angle recomputed, nearer the eye's middle) the scene's wide view showed too much of its
      // side: the tower is drawn as seen from about 17 degrees off the axis, at its true size, and slid to its
      // place on screen
      const vk = Math.min( 1, 0.3 * tz / Math.max( 1, Math.abs( tx ) ) ), vx0 = tx * vk, slide = F * ( tx - vx0 ) / tz;
      // (owner: Big Ben on the road's line, as the row at the left) it stands on the row's line 100 m off, at
      // the size it had on screen: its foot comes down to the row's
      const onRow = F * this.eye * ( 1 / 100 - 1 / tz );
      const P = ( x: number, y: number, z: number ): [ number, number ] => { const [ sx, sy ] = this.P( x - tx + vx0, y, z ); return [ sx + slide, sy + onRow ]; };
      // a lit tone by height: brightest low and at the clock, darker toward the spire
      const litAt = ( y: number ) => this.mix( this.mix( goldLo, gold, Math.max( 0, 1 - y / 110 ) ), goldHi, y < 20 ? 0.35 * ( 1 - y / 20 ) : 0 );
      const F0 = ( x0: number, x1: number, y0: number, y1: number, d = 0, col?: RGB ) => {
        if ( col ) { poly( [ P( x0, y0, tz - d ), P( x1, y0, tz - d ), P( x1, y1, tz - d ), P( x0, y1, tz - d ) ], rgb( col ) ); return; }
        const fg = g.createLinearGradient( 0, P( 0, y1, tz )[ 1 ], 0, P( 0, y0, tz )[ 1 ] );
        fg.addColorStop( 0, rgb( litAt( y1 ) ) );
        fg.addColorStop( 1, rgb( litAt( y0 ) ) );
        poly( [ P( x0, y0, tz - d ), P( x1, y0, tz - d ), P( x1, y1, tz - d ), P( x0, y1, tz - d ) ], fg );
      };
      // the side face: a darker copy in perspective (it stands to our right, its left face shows)
      const S0 = ( xs: number, y0: number, y1: number, z0: number, z1: number, k = 0.45 ) => {
        poly( [ P( xs, y0, z0 ), P( xs, y0, z1 ), P( xs, y1, z1 ), P( xs, y1, z0 ) ], rgb( this.mix( litAt( ( y0 + y1 ) / 2 ), night, k ) ) );
      };
      const line = ( pts: [ number, number ][], col: RGB, wd: number, a = 1 ) => { g.strokeStyle = rgb( col, a ); g.lineWidth = Math.max( 0.4, wd ); g.beginPath(); pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.stroke(); };
      const shadeC = this.mix( goldLo, night, 0.25 );
      // plinth and shaft
      S0( tx - half - 0.4, 0, 3, tz, tz + w + 0.4, 0.5 );
      F0( tx - half - 0.4, tx + half + 0.4, 0, 3, 0.2 );
      S0( tx - half, 3, 55, tz, tz + w );
      F0( tx - half, tx + half, 3, 55 );
      // the corner buttresses, lit on their faces, a shadow line beside them
      for ( const [ bx0, bx1 ] of [ [ tx - half, tx - half + 0.9 ], [ tx + half - 0.9, tx + half ] ] ) {
        F0( bx0, bx1, 3, 56, 0.25 );
        line( [ P( bx1 + ( bx0 < tx ? 0.05 : -0.95 ), 3, tz - 0.05 ), P( bx1 + ( bx0 < tx ? 0.05 : -0.95 ), 55, tz - 0.05 ) ], shadeC, s * 0.12, 0.8 );
      }
      // the storeys: string courses, pointed panels with their pairs of lancets, the friezes
      const bays = 3, bw = ( w - 1.8 ) / bays;
      for ( let y = 4; y < 54; y += 5.6 ) {
        line( [ P( tx - half, y, tz - 0.06 ), P( tx + half, y, tz - 0.06 ) ], this.mix( goldHi, gold, 0.4 ), s * 0.22 );
        line( [ P( tx - half, y + 0.25, tz - 0.06 ), P( tx + half, y + 0.25, tz - 0.06 ) ], shadeC, s * 0.1, 0.8 );
        for ( let q = 0; q < bays; q++ ) {
          const x0 = tx - half + 0.9 + q * bw + 0.25, x1 = x0 + bw - 0.5, xm = ( x0 + x1 ) / 2, top = y + 4.6;
          // the panel's pointed head
          line( [ P( x0, y + 0.6, tz - 0.05 ), P( x0, top - 1, tz - 0.05 ), P( xm, top, tz - 0.05 ), P( x1, top - 1, tz - 0.05 ), P( x1, y + 0.6, tz - 0.05 ) ], shadeC, s * 0.1, 0.9 );
          // its two lancets, dark glass, sharp heads
          for ( const [ l0, l1 ] of [ [ x0 + 0.25, xm - 0.15 ], [ xm + 0.15, x1 - 0.25 ] ] ) {
            const lm = ( l0 + l1 ) / 2, lt = top - 1.4;
            poly( [ P( l0, y + 1, tz - 0.04 ), P( l1, y + 1, tz - 0.04 ), P( l1, lt - 0.5, tz - 0.04 ), P( lm, lt, tz - 0.04 ), P( l0, lt - 0.5, tz - 0.04 ) ], rgb( this.mix( [ 60, 40, 30 ], goldLo, 0.3 ) ) );
          }
        }
        // every other storey a little frieze of quatrefoils
        if ( ( Math.round( ( y - 4 ) / 5.6 ) % 2 ) === 1 && s * 0.6 > 1.2 ) {
          for ( let x = tx - half + 1.2; x < tx + half - 1; x += 1.0 ) { const [ qx, qy ] = P( x, y - 0.55, tz - 0.06 ); g.strokeStyle = rgb( shadeC, 0.8 ); g.lineWidth = Math.max( 0.4, s * 0.06 ); g.beginPath(); g.arc( qx, qy, s * 0.3, 0, Math.PI * 2 ); g.stroke(); }
        }
      }
      // the shaft's side face: its string courses foreshortened
      for ( let y = 4; y < 54; y += 5.6 ) { line( [ P( tx - half, y, tz ), P( tx - half, y, tz + w ) ], this.mix( goldLo, gold, 0.3 ), s * 0.15, 0.8 ); }
      // the pierced parapet at the shaft's head
      F0( tx - half - 0.3, tx + half + 0.3, 55, 57.2, 0.35 );
      for ( let x = tx - half; x < tx + half - 0.4; x += 0.8 ) { poly( [ P( x + 0.2, 55.5, tz - 0.4 ), P( x + 0.55, 55.5, tz - 0.4 ), P( x + 0.55, 56.8, tz - 0.4 ), P( x + 0.2, 56.8, tz - 0.4 ) ], rgb( shadeC ) ); }
      // the clock stage: standing a little proud, the dial in its gilt square
      const cw = half + 0.7;
      S0( tx - cw, 57.2, 73, tz - 0.7, tz + w + 0.7, 0.4 );
      F0( tx - cw, tx + cw, 57.2, 73, 0.7 );
      {
        const cy0 = 65, dr = 3.5, sq = 4.4;
        // the gilt square frame and its traceried spandrels
        poly( [ P( tx - sq, cy0 - sq, tz - 0.75 ), P( tx + sq, cy0 - sq, tz - 0.75 ), P( tx + sq, cy0 + sq, tz - 0.75 ), P( tx - sq, cy0 + sq, tz - 0.75 ) ], rgb( this.mix( goldHi, gold, 0.25 ) ) );
        const [ ccx, ccy ] = P( tx, cy0, tz - 0.8 ), cr = s * dr;
        g.strokeStyle = rgb( goldLo, 0.9 );
        g.lineWidth = Math.max( 0.4, s * 0.1 );
        // the spandrels: dark grounds with gilt roundels and leafwork, as on the tower
        for ( const [ dx, dy ] of [ [ -1, -1 ], [ 1, -1 ], [ -1, 1 ], [ 1, 1 ] ] ) {
          poly( [ P( tx + dx * sq, cy0 + dy * sq, tz - 0.76 ), P( tx + dx * sq * 0.35, cy0 + dy * sq, tz - 0.76 ), P( tx + dx * sq, cy0 + dy * sq * 0.35, tz - 0.76 ) ], rgb( [ 30, 34, 44 ] ) );
          const [ kx, ky ] = P( tx + dx * sq * 0.8, cy0 + dy * sq * 0.8, tz - 0.8 );
          g.fillStyle = rgb( goldHi );
          g.beginPath(); g.arc( kx, ky, s * 0.42, 0, Math.PI * 2 ); g.fill();
          g.strokeStyle = rgb( gold );
          g.lineWidth = Math.max( 0.4, s * 0.08 );
          for ( let q = 0; q < 4; q++ ) { const a = q * Math.PI / 2 + Math.PI / 4; g.beginPath(); g.moveTo( kx + Math.cos( a ) * s * 0.5, ky + Math.sin( a ) * s * 0.5 ); g.lineTo( kx + Math.cos( a ) * s * 0.9, ky + Math.sin( a ) * s * 0.9 ); g.stroke(); }
        }
        glow( ccx, ccy, cr * 2.6, [ 255, 250, 225 ], 0.3 );
        // (owner: Big Ben done better, after real pictures) one dial, drawn in its own unit circle and laid on a
        // face by an affine map (the front square on, the side foreshortened): the white opal glass in its gilt
        // rim; the chapter ring between dark lines holding the Roman hours and the minute marks; the centre's
        // cast iron tracery; the hands dark and shaped
        const dial = ( ma: number, mb: number, mc: number, md: number, me: number, mf: number, pxr: number ) => {
          const px = 1 / Math.max( 1, pxr ), lw = ( k2: number, min = 0.45 ) => Math.max( min * px, k2 );
          g.save();
          g.transform( ma, mb, mc, md, me, mf );
          const disc = ( rk: number, col: string ) => { g.fillStyle = col; g.beginPath(); g.arc( 0, 0, rk, 0, Math.PI * 2 ); g.fill(); };
          disc( 1.07, rgb( goldLo ) );
          disc( 1.03, rgb( this.mix( goldHi, gold, 0.3 ) ) );
          const cf = g.createRadialGradient( -0.25, -0.25, 0, 0, 0, 1 );
          cf.addColorStop( 0, rgb( [ 255, 254, 246 ] ) );
          cf.addColorStop( 1, rgb( [ 240, 236, 214 ] ) );
          disc( 1, cf as unknown as string );
          const ink: RGB = [ 26, 30, 40 ];
          g.strokeStyle = rgb( ink );
          for ( const rk of [ 0.97, 0.9, 0.72, 0.68 ] ) { g.lineWidth = lw( rk > 0.95 || rk < 0.7 ? 0.03 : 0.015 ); g.beginPath(); g.arc( 0, 0, rk, 0, Math.PI * 2 ); g.stroke(); }
          g.lineWidth = lw( 0.012, 0.3 );
          for ( let q = 0; q < 60; q++ ) { const an = q / 60 * Math.PI * 2; g.beginPath(); g.moveTo( Math.cos( an ) * 0.9, Math.sin( an ) * 0.9 ); g.lineTo( Math.cos( an ) * 0.96, Math.sin( an ) * 0.96 ); g.stroke(); }
          const glyphs = [ 'XII', 'I', 'II', 'III', 'IIII', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI' ];
          for ( let h2 = 0; h2 < 12; h2++ ) {
            const an = h2 / 12 * Math.PI * 2 - Math.PI / 2, gl = glyphs[ h2 ], gh = 0.15, gw = gh * 0.32;
            g.save();
            g.translate( Math.cos( an ) * 0.81, Math.sin( an ) * 0.81 );
            g.rotate( an + Math.PI / 2 );
            g.lineWidth = lw( gh * 0.18, 0.4 );
            let xg = -( gl.length - 1 ) * gw * 0.6;
            for ( const ch of gl ) {
              g.beginPath();
              if ( ch === 'I' ) { g.moveTo( xg, -gh / 2 ); g.lineTo( xg, gh / 2 ); }
              else if ( ch === 'V' ) { g.moveTo( xg - gw * 0.6, -gh / 2 ); g.lineTo( xg, gh / 2 ); g.lineTo( xg + gw * 0.6, -gh / 2 ); }
              else { g.moveTo( xg - gw * 0.6, -gh / 2 ); g.lineTo( xg + gw * 0.6, gh / 2 ); g.moveTo( xg + gw * 0.6, -gh / 2 ); g.lineTo( xg - gw * 0.6, gh / 2 ); }
              g.stroke();
              xg += gw * 1.2;
            }
            g.restore();
          }
          g.strokeStyle = rgb( this.mix( goldLo, ink, 0.3 ), 0.65 );
          g.lineWidth = lw( 0.01, 0.3 );
          for ( let q = 0; q < 24; q++ ) { const an = q / 24 * Math.PI * 2; g.beginPath(); g.moveTo( Math.cos( an ) * 0.12, Math.sin( an ) * 0.12 ); g.lineTo( Math.cos( an ) * 0.66, Math.sin( an ) * 0.66 ); g.stroke(); }
          for ( const rk of [ 0.28, 0.46 ] ) {
            for ( let q = 0; q < 24; q++ ) {
              const a0 = q / 24 * Math.PI * 2, a1 = ( q + 1 ) / 24 * Math.PI * 2, am = ( a0 + a1 ) / 2;
              g.beginPath(); g.moveTo( Math.cos( a0 ) * rk, Math.sin( a0 ) * rk ); g.quadraticCurveTo( Math.cos( am ) * ( rk + 0.08 ), Math.sin( am ) * ( rk + 0.08 ), Math.cos( a1 ) * rk, Math.sin( a1 ) * rk ); g.stroke();
            }
          }
          g.fillStyle = rgb( ink );
          const hand = ( an: number, len: number, wd: number ) => {
            const ca = Math.cos( an ), sa = Math.sin( an ), nx = -sa, ny = ca;
            g.beginPath();
            g.moveTo( -ca * len * 0.12 + nx * wd, -sa * len * 0.12 + ny * wd );
            g.lineTo( ca * len * 0.7 + nx * wd * 0.6, sa * len * 0.7 + ny * wd * 0.6 );
            g.lineTo( ca * len, sa * len );
            g.lineTo( ca * len * 0.7 - nx * wd * 0.6, sa * len * 0.7 - ny * wd * 0.6 );
            g.lineTo( -ca * len * 0.12 - nx * wd, -sa * len * 0.12 - ny * wd );
            g.closePath(); g.fill();
          };
          hand( -0.5, 0.5, Math.max( 0.05, 0.8 * px ) );
          hand( -1.9, 0.8, Math.max( 0.028, 0.6 * px ) );
          disc( 0.05, rgb( ink ) );
          g.restore();
        };
        dial( cr, 0, 0, cr, ccx, ccy, cr );
        // the inscription band over the dial, gilt dots, and under it a row of small arches
        for ( let x = tx - sq + 0.4; x < tx + sq - 0.3; x += 0.55 ) { const [ ix, iy ] = P( x, cy0 + sq + 0.55, tz - 0.76 ); g.fillStyle = rgb( goldHi, 0.85 ); g.fillRect( ix, iy, Math.max( 0.8, s * 0.3 ), Math.max( 0.8, s * 0.35 ) ); }
        // (owner: the side's clock drawn as well) the same dial on the side face, in its own gilt square and
        // spandrels, foreshortened with the face
        {
          const xs = tx - cw - 0.02, zc = tz + half;
          poly( [ P( xs, cy0 - sq, zc - sq ), P( xs, cy0 - sq, zc + sq ), P( xs, cy0 + sq, zc + sq ), P( xs, cy0 + sq, zc - sq ) ], rgb( this.mix( this.mix( goldHi, gold, 0.25 ), night, 0.3 ) ) );
          for ( const [ dz, dy ] of [ [ -1, -1 ], [ 1, -1 ], [ -1, 1 ], [ 1, 1 ] ] ) {
            poly( [ P( xs - 0.01, cy0 + dy * sq, zc + dz * sq ), P( xs - 0.01, cy0 + dy * sq, zc + dz * sq * 0.35 ), P( xs - 0.01, cy0 + dy * sq * 0.35, zc + dz * sq ) ], rgb( this.mix( [ 30, 34, 44 ], night, 0.3 ) ) );
          }
          const [ sx, sy ] = P( xs - 0.03, cy0, zc ), [ ux, uy ] = P( xs - 0.03, cy0, zc + dr ), [ vx2, vy2 ] = P( xs - 0.03, cy0 - dr, zc );
          glow( sx, sy, cr * 1.8, [ 255, 250, 225 ], 0.18 );
          dial( ux - sx, uy - sy, vx2 - sx, vy2 - sy, sx, sy, Math.max( Math.abs( ux - sx ), 1 ) );
          // the side's inscription band
          for ( let zz = zc - sq + 0.4; zz < zc + sq - 0.3; zz += 0.55 ) { const [ ix, iy ] = P( xs - 0.02, cy0 + sq + 0.55, zz ); g.fillStyle = rgb( goldHi, 0.7 ); g.fillRect( ix, iy, Math.max( 0.6, s * 0.15 ), Math.max( 0.8, s * 0.35 ) ); }
        }
      }
      // the belfry: three lancets lit between gilt piers, its own parapet (owner: the lit part red, then
      // yellow instead)
      S0( tx - half, 74.4, 81, tz, tz + w, 0.5 );
      F0( tx - half, tx + half, 74.4, 81, 0.1 );
      const lb = ( w - 2.4 ) / 3;
      for ( let q = 0; q < 3; q++ ) {
        const x0 = tx - half + 1.2 + q * lb + 0.25, x1 = x0 + lb - 0.5, xm = ( x0 + x1 ) / 2;
        poly( [ P( x0, 75, tz - 0.15 ), P( x1, 75, tz - 0.15 ), P( x1, 79, tz - 0.15 ), P( xm, 80.4, tz - 0.15 ), P( x0, 79, tz - 0.15 ) ], rgb( [ 255, 220, 110 ] ) );
        line( [ P( xm, 75, tz - 0.16 ), P( xm, 79.6, tz - 0.16 ) ], goldLo, s * 0.15 );
      }
      glow( P( tx, 77.5, tz )[ 0 ], P( tx, 77.5, tz )[ 1 ], s * 10, [ 255, 206, 96 ], 0.14 );
      // the gallery over the clock and the corner pinnacles, tall gilt spikes
      F0( tx - cw - 0.2, tx + cw + 0.2, 73, 74.4, 0.9 );
      for ( let x = tx - cw; x < tx + cw - 0.3; x += 0.7 ) { poly( [ P( x + 0.15, 73.3, tz - 0.95 ), P( x + 0.45, 73.3, tz - 0.95 ), P( x + 0.45, 74.1, tz - 0.95 ), P( x + 0.15, 74.1, tz - 0.95 ) ], rgb( shadeC ) ); }
      for ( const cx of [ tx - cw, tx + cw ] ) {
        F0( cx - 0.55, cx + 0.55, 74.4, 78, 0.95 );
        poly( [ P( cx - 0.55, 78, tz - 0.95 ), P( cx + 0.55, 78, tz - 0.95 ), P( cx, 82.5, tz - 0.95 ) ], rgb( this.mix( gold, goldHi, 0.3 ) ) );
      }
      // the spire: a steep pyramid of dark gilt, its corner ribs and two tiers of lucarnes, the finial
      {
        const y0 = 81, y1 = 96, apex = P( tx, y1, tz + half );
        const spF = g.createLinearGradient( 0, apex[ 1 ], 0, P( 0, y0, tz )[ 1 ] );
        spF.addColorStop( 0, rgb( this.mix( goldLo, night, 0.3 ) ) );
        spF.addColorStop( 1, rgb( this.mix( goldLo, gold, 0.45 ) ) );
        poly( [ P( tx - half, y0, tz ), P( tx + half, y0, tz ), apex ], spF );
        poly( [ P( tx - half, y0, tz ), apex, P( tx - half, y0, tz + w ) ], rgb( this.mix( goldLo, night, 0.5 ) ) );
        line( [ P( tx - half, y0, tz ), apex, P( tx + half, y0, tz ) ], this.mix( gold, goldHi, 0.3 ), s * 0.25 );
        // the lucarnes: little gabled windows on the spire's face, three low, two higher
        for ( const [ yy, n, sc ] of [ [ 83.2, 3, 1 ], [ 87.6, 2, 0.75 ] ] as [ number, number, number ][] ) {
          const span = w * ( 1 - ( yy - y0 ) / ( y1 - y0 ) ) * 0.8;
          for ( let q = 0; q < n; q++ ) {
            const lx = tx - span / 2 + span * ( q + 0.5 ) / n, lz = tz + ( yy - y0 ) / ( y1 - y0 ) * half;
            poly( [ P( lx - 0.6 * sc, yy, lz - 0.2 ), P( lx + 0.6 * sc, yy, lz - 0.2 ), P( lx + 0.6 * sc, yy + 1.4 * sc, lz - 0.2 ), P( lx, yy + 2.4 * sc, lz - 0.2 ), P( lx - 0.6 * sc, yy + 1.4 * sc, lz - 0.2 ) ], rgb( this.mix( gold, goldHi, 0.2 ) ) );
            poly( [ P( lx - 0.3 * sc, yy + 0.2, lz - 0.21 ), P( lx + 0.3 * sc, yy + 0.2, lz - 0.21 ), P( lx + 0.3 * sc, yy + 1.3 * sc, lz - 0.21 ), P( lx - 0.3 * sc, yy + 1.3 * sc, lz - 0.21 ) ], rgb( [ 70, 50, 34 ] ) );
          }
        }
        // the finial: a gilt shaft, the orb, the cross
        const [ fx, fy ] = apex, [ , fy2 ] = P( tx, y1 + 3.2, tz + half ), [ , fo ] = P( tx, y1 + 2.2, tz + half );
        line( [ [ fx, fy ], [ fx, fy2 ] ], goldHi, s * 0.25 );
        g.fillStyle = rgb( goldHi );
        g.beginPath(); g.arc( fx, fo, Math.max( 0.8, s * 0.35 ), 0, Math.PI * 2 ); g.fill();
        line( [ [ fx - s * 0.4, fy2 + s * 0.45 ], [ fx + s * 0.4, fy2 + s * 0.45 ] ], goldHi, s * 0.15 );
      }
      // (owner: Big Ben's quality again) a pass of finer work over the tower:
      // the recesses of the shaft's panels in shade, so the tracery stands proud
      for ( let y = 4; y < 54; y += 5.6 ) {
        for ( let q = 0; q < bays; q++ ) {
          const x0 = tx - half + 0.9 + q * bw + 0.25, x1 = x0 + bw - 0.5, xm = ( x0 + x1 ) / 2, top = y + 4.6;
          const pts: [ number, number ][] = [ P( x0 + 0.08, y + 0.65, tz - 0.03 ), P( x0 + 0.08, top - 1.05, tz - 0.03 ), P( xm, top - 0.08, tz - 0.03 ), P( x1 - 0.08, top - 1.05, tz - 0.03 ), P( x1 - 0.08, y + 0.65, tz - 0.03 ) ];
          const [ , pyTop ] = P( 0, top, tz ), [ , pyBot ] = P( 0, y, tz );
          const rg = g.createLinearGradient( 0, pyTop, 0, pyBot );
          rg.addColorStop( 0, rgb( this.mix( goldLo, night, 0.25 ), 0.55 ) );
          rg.addColorStop( 1, rgb( this.mix( goldLo, night, 0.1 ), 0.25 ) );
          poly( pts, rg );
          // the lancets' light sills
          line( [ P( x0 + 0.25, y + 1, tz - 0.06 ), P( x1 - 0.25, y + 1, tz - 0.06 ) ], goldHi, s * 0.1, 0.8 );
        }
        // the buttresses' little gablets at every storey
        for ( const bx0 of [ tx - half, tx + half - 0.9 ] ) {
          poly( [ P( bx0 - 0.05, y + 2.6, tz - 0.3 ), P( bx0 + 0.95, y + 2.6, tz - 0.3 ), P( bx0 + 0.45, y + 3.6, tz - 0.3 ) ], rgb( this.mix( goldHi, gold, 0.4 ) ) );
          line( [ P( bx0 + 0.45, y + 3.6, tz - 0.31 ), P( bx0 + 0.45, y + 4.1, tz - 0.31 ) ], goldHi, s * 0.08 );
        }
      }
      // the lit edge down the front's left corner, the shade down its right
      line( [ P( tx - half, 3, tz - 0.27 ), P( tx - half, 57, tz - 0.27 ) ], goldHi, s * 0.2, 0.9 );
      line( [ P( tx + half, 3, tz - 0.27 ), P( tx + half, 57, tz - 0.27 ) ], goldLo, s * 0.2, 0.9 );
      // the side face's lancets, foreshortened slits per storey
      for ( let y = 4; y < 54; y += 5.6 ) {
        for ( const dz of [ 2.2, 4.4, 7.6, 9.8 ] ) {
          poly( [ P( tx - half - 0.02, y + 1, tz + dz ), P( tx - half - 0.02, y + 1, tz + dz + 0.9 ), P( tx - half - 0.02, y + 3.6, tz + dz + 0.9 ), P( tx - half - 0.02, y + 3.9, tz + dz + 0.45 ), P( tx - half - 0.02, y + 3.6, tz + dz ) ], rgb( this.mix( [ 50, 34, 26 ], night, 0.4 ) ) );
        }
      }
      // under the dial a band of gilt shields in their panels
      for ( let x = tx - cw + 0.8; x < tx + cw - 0.6; x += 1.15 ) {
        poly( [ P( x, 58.2, tz - 0.76 ), P( x + 0.7, 58.2, tz - 0.76 ), P( x + 0.7, 59.3, tz - 0.76 ), P( x + 0.35, 59.7, tz - 0.76 ), P( x, 59.3, tz - 0.76 ) ], rgb( this.mix( goldHi, gold, 0.3 ) ) );
      }
      // the belfry's lancets crowned with little trefoils and gabled hoods
      for ( let q = 0; q < 3; q++ ) {
        const x0 = tx - half + 1.2 + q * lb + 0.25, x1 = x0 + lb - 0.5, xm = ( x0 + x1 ) / 2;
        line( [ P( x0 - 0.2, 79, tz - 0.2 ), P( xm, 81.4, tz - 0.2 ), P( x1 + 0.2, 79, tz - 0.2 ) ], goldHi, s * 0.15 );
        const [ tfx, tfy ] = P( xm, 79.7, tz - 0.17 );
        g.strokeStyle = rgb( goldLo );
        g.lineWidth = Math.max( 0.4, s * 0.08 );
        g.beginPath(); g.arc( tfx, tfy, s * 0.35, 0, Math.PI * 2 ); g.stroke();
      }
      // the spire's ironwork: bands across it and crockets of gilt up its ribs
      {
        const y0 = 81, y1 = 96, apex = P( tx, y1, tz + half );
        for ( let yy = y0 + 2.5; yy < y1 - 1; yy += 2.6 ) {
          const u = ( yy - y0 ) / ( y1 - y0 ), hw2 = half * ( 1 - u ), zz = tz + half * u;
          line( [ P( tx - hw2, yy, zz ), P( tx + hw2, yy, zz ) ], this.mix( gold, goldLo, 0.4 ), s * 0.1, 0.8 );
        }
        for ( const sx of [ -1, 1 ] ) {
          for ( let q = 1; q < 7; q++ ) {
            const u = q / 7, [ ax, ay ] = P( tx + sx * half * ( 1 - u ), y0 + ( y1 - y0 ) * u, tz + half * u );
            g.fillStyle = rgb( goldHi );
            g.beginPath(); g.arc( ax + sx * s * 0.25, ay, Math.max( 0.7, s * 0.22 ), 0, Math.PI * 2 ); g.fill();
          }
        }
        void apex;
      }
      // the floodlights' warmth round the tower in the damp air
      const [ gx, gy ] = P( tx, 45, tz );
      glow( gx, gy, s * 42, [ 236, 176, 84 ], 0.07 );
    }
    // ---- (kaizen) over the row's roofs at the left: Westminster Abbey's twin west towers floodlit pale gold,
    // and the dark dome of the Central Hall with its lantern ----
    {
      // (owner: the two towers removed)
      // (owner: the domed building done properly) the Methodist Central Hall: its stone body of pilasters and
      // lit windows, the drum ringed with paired columns and arched windows under a cornice, the great lead
      // dome with its ribs and its round lucarnes catching a little light, the columned lantern and its finial
      {
        const dx0 = -150, dz = 150, dr = 16, s2 = F / dz, stoneC: RGB = this.mix( [ 196, 188, 170 ], night, 0.5 );
        // (owner: the domed building 30 px higher) its stone body taller by those 30 px, so it still stands
        const body = 24 + 30 / s2, drum0 = body, drum1 = body + 9, dome0 = drum1 + 1.2;
        front( dx0 - 30, dx0 + 30, dz, body, 0, rr( 917 ) );
        // the drum
        const [ d0x ] = P( dx0 - dr, 0, dz ), [ d1x ] = P( dx0 + dr, 0, dz ), [ , dyTop ] = P( 0, drum1, dz ), [ , dyBot ] = P( 0, drum0, dz );
        const dg2 = g.createLinearGradient( d0x, 0, d1x, 0 );
        dg2.addColorStop( 0, rgb( this.mix( stoneC, night, 0.5 ) ) );
        dg2.addColorStop( 0.45, rgb( this.mix( stoneC, warmWin, 0.12 ) ) );
        dg2.addColorStop( 1, rgb( this.mix( stoneC, night, 0.55 ) ) );
        g.fillStyle = dg2;
        g.fillRect( d0x, dyTop, d1x - d0x, dyBot - dyTop );
        for ( let q = 0; q < 8; q++ ) {
          const u = -0.85 + q * 1.7 / 7, wx = d0x + ( d1x - d0x ) * ( 0.5 + u * 0.5 ), ww = ( d1x - d0x ) * 0.035 * Math.sqrt( 1 - u * u );
          g.fillStyle = rgb( this.mix( stoneC, [ 255, 240, 210 ], 0.2 ) );
          g.fillRect( wx - ww * 1.6, dyTop + 2, ww * 0.5, dyBot - dyTop - 3 );
          g.fillRect( wx + ww * 1.1, dyTop + 2, ww * 0.5, dyBot - dyTop - 3 );
          if ( q < 7 ) {
            const ax = d0x + ( d1x - d0x ) * ( 0.5 + ( u + 0.85 / 7 * 1.0 ) * 0.5 ), aw = ( d1x - d0x ) * 0.03 * Math.sqrt( Math.max( 0, 1 - ( u + 0.12 ) * ( u + 0.12 ) ) );
            g.fillStyle = rgb( q % 3 === 1 ? this.mix( warmWin, night, 0.3 ) : this.mix( [ 40, 46, 60 ], night, 0.3 ) );
            g.beginPath(); g.moveTo( ax - aw, dyBot - 1.5 ); g.lineTo( ax - aw, dyTop + ( dyBot - dyTop ) * 0.45 ); g.arc( ax, dyTop + ( dyBot - dyTop ) * 0.45, aw, Math.PI, 0 ); g.lineTo( ax + aw, dyBot - 1.5 ); g.closePath(); g.fill();
          }
        }
        g.fillStyle = rgb( this.mix( stoneC, [ 255, 240, 210 ], 0.15 ) );
        g.fillRect( d0x - s2 * 0.6, dyTop - s2 * 0.8, d1x - d0x + s2 * 1.2, s2 * 0.9 );
        // the dome: lead blue grey, lit faintly from the city below, its ribs and lucarnes
        const [ dcx, dcy ] = P( dx0, dome0, dz ), drp = ( d1x - d0x ) / 2 * 0.96, drh = drp * 0.92;
        const dg = g.createRadialGradient( dcx - drp * 0.3, dcy - drh * 0.7, 0, dcx, dcy - drh * 0.2, drp * 1.15 );
        dg.addColorStop( 0, rgb( this.mix( [ 120, 130, 146 ], steel, 0.35 ) ) );
        dg.addColorStop( 0.6, rgb( this.mix( [ 60, 66, 80 ], night, 0.35 ) ) );
        dg.addColorStop( 1, rgb( this.mix( night, [ 24, 26, 34 ], 0.5 ) ) );
        g.fillStyle = dg;
        g.beginPath(); g.ellipse( dcx, dcy, drp, drh, 0, Math.PI, 0 ); g.closePath(); g.fill();
        g.strokeStyle = rgb( this.mix( [ 150, 160, 176 ], night, 0.35 ), 0.7 );
        g.lineWidth = Math.max( 0.5, s2 * 0.12 );
        for ( let q = 1; q < 8; q++ ) {
          const u = -1 + q * 2 / 8;
          g.beginPath(); g.ellipse( dcx, dcy, Math.abs( u ) * drp, drh, 0, u < 0 ? Math.PI : Math.PI * 1.5, u < 0 ? Math.PI * 1.5 : Math.PI * 2 ); g.stroke();
        }
        for ( let q = 0; q < 5; q++ ) {
          const u = -0.6 + q * 0.3, lx = dcx + u * drp * 0.92, ly = dcy - drh * 0.18 * ( 1 - Math.abs( u ) * 0.5 );
          g.fillStyle = rgb( this.mix( stoneC, night, 0.2 ) );
          g.beginPath(); g.arc( lx, ly, drp * 0.06 * Math.sqrt( 1 - u * u ), 0, Math.PI * 2 ); g.fill();
          g.fillStyle = rgb( q === 2 ? this.mix( warmWin, night, 0.35 ) : this.mix( [ 36, 40, 54 ], night, 0.3 ) );
          g.beginPath(); g.arc( lx, ly, drp * 0.035 * Math.sqrt( 1 - u * u ), 0, Math.PI * 2 ); g.fill();
        }
        // the lantern: a little columned drum, its cap, the finial
        const lw2 = drp * 0.18, lh2 = drp * 0.32, [ lcx, ltop ] = [ dcx, dcy - drh ];
        g.fillStyle = rgb( this.mix( stoneC, night, 0.25 ) );
        g.fillRect( lcx - lw2, ltop - lh2, lw2 * 2, lh2 );
        for ( let q = 0; q < 4; q++ ) { g.fillStyle = rgb( this.mix( warmWin, night, 0.3 ) ); g.fillRect( lcx - lw2 * 0.8 + q * lw2 * 0.45, ltop - lh2 * 0.8, lw2 * 0.22, lh2 * 0.6 ); }
        g.fillStyle = rgb( this.mix( [ 70, 76, 90 ], night, 0.3 ) );
        g.beginPath(); g.ellipse( lcx, ltop - lh2, lw2 * 1.1, lh2 * 0.55, 0, Math.PI, 0 ); g.fill();
        g.strokeStyle = rgb( this.mix( gold, night, 0.3 ) );
        g.lineWidth = Math.max( 0.6, s2 * 0.2 );
        g.beginPath(); g.moveTo( lcx, ltop - lh2 * 1.5 ); g.lineTo( lcx, ltop - lh2 * 2.3 ); g.stroke();
        g.fillStyle = rgb( this.mix( gold, night, 0.3 ) );
        g.beginPath(); g.arc( lcx, ltop - lh2 * 1.75, Math.max( 0.8, s2 * 0.4 ), 0, Math.PI * 2 ); g.fill();
      }
    }
    // ---- the row of buildings across the square at the left and the middle, flush, their heights varying ----
    {
      const r = rr( 37 ), z = 100;
      const items: [ number, number, number, number, number, number ][] = [];
      // (owner: no thin sliver of a building by the tower) the row ends with whole buildings, a clear gap left
      // before the tower: the last one is kept only if most of it fits
      const limit = ( TX - TOWER.w * 1.5 ) * z / TOWER.z;
      for ( let x = -W * z / F * 0.62 - 20; x < limit; ) {
        // (owner: every building on one line) no fronts set back
        const w = 18 + r() * 16, h = 20 + r() * 12, st = r() < 0.35 ? 0 : r() < 0.55 ? 1 : 2, dz = r() < 0.3 ? 0 : 0;
        if ( x + w > limit && limit - x < 16 ) { break; }
        items.push( [ x, Math.min( limit, x + w ), z + dz, h, st, 16 ] );
        x += w;
      }
      // (owner: the right of Big Ben as the left, on the road's line) past the palace's end and 60 px of room
      // the row carries on
      {
        const r2 = rr( 38 );
        // (owner: that building ran too far back on its left) the first one only 6 m deep
        for ( let x = this.rightStart( TX, z + 6 ), first = true; x < W * z / F * 0.62 + 20; first = false ) {
          const w = 18 + r2() * 16, h0 = 20 + r2() * 12, st0 = r2() < 0.35 ? 0 : r2() < 0.55 ? 1 : 2;
          // (owner: the one after the palace a low one, not the big grey) a low brick front
          const h = first ? 20 : h0, st = first ? 1 : st0;
          items.push( [ x, x + w, z, h, st, first ? 6 : 16 ] );
          x += w;
        }
      }
      // (owner: the buildings' order, nothing spilling over) the ones standing back drawn first, then from the
      // outside in, so each end toward the middle shows only where a neighbour stands back
      items.sort( ( a2, b2 ) => ( b2[ 2 ] - a2[ 2 ] ) || ( Math.abs( b2[ 0 ] + b2[ 1 ] ) - Math.abs( a2[ 0 ] + a2[ 1 ] ) ) );
      for ( const [ x0, x1, zz, h, st, dp ] of items ) { endOf( x0, x1, zz, h, st, dp ); }
      for ( const [ x0, x1, zz, h, st ] of items ) { front( x0, x1, zz, h, st, rr( Math.round( x0 * 7 ) + 400 ) ); }
    }
    // ---- the square: its trees, lit from below by the lamps, and its lamps ----
    {
      // (owner: the trees in detail) plane trees in leaf, lit from below by the square's lamps: each crown
      // a heap of leaf masses, the lower ones warm in the lamplight, the upper ones dark against the night,
      // small leaves along their rims; the trunk and its forks under them
      const r = rr( 13 );
      const trees: [ number, number, number ][] = [];
      for ( let q = 0; q < 24; q++ ) {
        const z = 55 + r() * 38, x = ( -W * z / F * 0.55 ) + r() * ( W * z / F * 0.55 + 40 );
        if ( x > TX * z / TOWER.z - 7 && x < TX * z / TOWER.z + 7 ) { continue; }
        trees.push( [ x, z, 3.4 + r() * 1.8 ] );
      }
      // (owner: trees after Big Ben at the right as at the left) as many to a metre, before the row past the
      // palace
      {
        const r2 = rr( 14 ), zm = 74, span = ( zz: number ) => W * zz / F * 0.55 - this.rightStart( TX, zz );
        const n = Math.max( 3, Math.round( 24 * span( zm ) / ( W * zm / F * 0.55 + 40 ) ) );
        for ( let q = 0; q < n; q++ ) {
          const z = 55 + r2() * 38, rad = 3.4 + r2() * 1.8, x = this.rightStart( TX, z ) + rad + r2() * Math.max( 0, span( z ) - rad );
          trees.push( [ x, z, rad ] );
        }
      }
      trees.sort( ( a2, b2 ) => b2[ 1 ] - a2[ 1 ] );
      for ( const [ x, z, rad ] of trees ) {
        const s = F / z, [ bx, by ] = P( x, 0, z ), [ cx, cy ] = P( x, 7.5, z ), rx = s * rad, ry = rx * 0.82;
        g.strokeStyle = rgb( this.mix( night, [ 52, 44, 36 ], 0.55 ) );
        g.lineCap = 'round';
        g.lineWidth = Math.max( 0.8, s * 0.35 );
        g.beginPath(); g.moveTo( bx, by ); g.lineTo( bx, cy + ry * 0.4 ); g.stroke();
        g.lineWidth = Math.max( 0.5, s * 0.18 );
        for ( const sd of [ -1, 1 ] ) { g.beginPath(); g.moveTo( bx, cy + ry * 0.6 ); g.quadraticCurveTo( bx + sd * rx * 0.2, cy + ry * 0.2, bx + sd * rx * 0.5, cy ); g.stroke(); }
        const masses: [ number, number, number ][] = [ [ cx, cy, 0.6 ] ];
        for ( let q = 0; q < 9; q++ ) { const a = q / 9 * Math.PI * 2 + r() * 0.5; masses.push( [ cx + Math.cos( a ) * rx * 0.55, cy + Math.sin( a ) * ry * 0.5, 0.38 + r() * 0.14 ] ); }
        masses.sort( ( a2, b2 ) => a2[ 1 ] - b2[ 1 ] );
        for ( const [ mx, my, mk ] of masses ) {
          const mr = rx * mk, low = Math.max( 0, Math.min( 1, ( my - cy + ry * 0.5 ) / ry ) );
          const top = this.mix( [ 22, 34, 30 ], night, 0.3 ), lamp2 = this.mix( [ 128, 140, 70 ], warmWin, 0.35 );
          const mg = g.createRadialGradient( mx, my + mr * 0.6, 0, mx, my, mr * 1.2 );
          mg.addColorStop( 0, rgb( this.mix( top, lamp2, 0.45 + 0.4 * low ) ) );
          mg.addColorStop( 0.6, rgb( this.mix( top, lamp2, 0.12 + 0.2 * low ) ) );
          mg.addColorStop( 1, rgb( top ) );
          g.fillStyle = mg;
          g.beginPath();
          for ( let w2 = 0; w2 <= 14; w2++ ) { const a = w2 / 14 * Math.PI * 2, kk = mr * ( 0.86 + 0.14 * Math.sin( a * 4 + mx ) ); if ( w2 ) { g.lineTo( mx + Math.cos( a ) * kk, my + Math.sin( a ) * kk ); } else { g.moveTo( mx + kk, my ); } }
          g.closePath(); g.fill();
          // small leaves along the rim, lit underneath, dark above
          if ( mr > 4 ) {
            for ( let q = 0; q < mr * 1.6; q++ ) {
              const a = r() * Math.PI * 2, d = mr * ( 0.82 + r() * 0.22 ), lx2 = mx + Math.cos( a ) * d, ly2 = my + Math.sin( a ) * d;
              // only the leaves the lamps light below show; the dark ones read as holes
              if ( Math.sin( a ) < 0.15 ) { continue; }
              g.fillStyle = rgb( this.mix( top, lamp2, 0.55 ) );
              g.beginPath(); g.ellipse( lx2, ly2, Math.max( 0.8, s * 0.22 ), Math.max( 0.6, s * 0.14 ), a, 0, Math.PI * 2 ); g.fill();
            }
          }
        }
      }
      // the far pavement's kerb, its railing
      const [ , ky ] = P( 0, 0, ROAD.far ), [ , ry1 ] = P( 0, 1.1, ROAD.far + 1 );
      g.fillStyle = rgb( this.mix( slate, pale, 0.15 ) );
      g.fillRect( 0, ky - 1, W, 2 );
      g.strokeStyle = rgb( [ 14, 14, 18 ] );
      g.lineWidth = 1;
      g.beginPath(); g.moveTo( 0, ry1 ); g.lineTo( W, ry1 ); g.stroke();
      for ( let x = 0; x < W; x += 5 ) { g.beginPath(); g.moveTo( x, ry1 ); g.lineTo( x, ky ); g.stroke(); }
    }
    // ---- the road: wet black asphalt, the tower's gold smeared on it, the lane's dashes ----
    {
      const [ , y0 ] = P( 0, 0, ROAD.far ), [ , y1 ] = P( 0, 0, ROAD.near );
      const rg = g.createLinearGradient( 0, y0, 0, y1 );
      rg.addColorStop( 0, rgb( this.mix( night, [ 40, 34, 34 ], 0.4 ) ) );
      rg.addColorStop( 1, rgb( this.mix( night, [ 24, 22, 26 ], 0.5 ) ) );
      g.fillStyle = rg;
      // down to the frame's foot: the pavement in front keeps its own scale and meets it lower
      g.fillRect( 0, y0, W, H - y0 );
      const [ tx ] = P( TX, 0, TOWER.z );
      const tg = g.createLinearGradient( 0, y0, 0, y1 );
      tg.addColorStop( 0, rgb( gold, 0.22 ) );
      tg.addColorStop( 1, rgb( gold, 0 ) );
      g.fillStyle = tg;
      g.fillRect( tx - F * 18 / TOWER.z, y0, F * 36 / TOWER.z, y1 - y0 );
      const mid = ( ROAD.near + ROAD.far ) / 2;
      for ( let x = -300; x < 300; x += 6 ) { poly( [ P( x, 0.01, mid - 0.1 ), P( x + 3, 0.01, mid - 0.1 ), P( x + 3, 0.01, mid + 0.1 ), P( x, 0.01, mid + 0.1 ) ], rgb( pale, 0.3 ) ); }
    }
    if ( lit ) { this.backLit = bk.c; return; }
    this.back = bk.c;

    // ---- the front layer four times: the street lamp and the telephone box dark, each lit alone, both lit
    // (owner: each flickers on its own) ----
    for ( const [ onL, onB ] of [ [ false, false ], [ true, false ], [ false, true ], [ true, true ] ] ) {
      const Ff = this.Ff, Pf = ( x: number, y: number, z: number ): [ number, number ] => [ this.vx + Ff * x / z, this.hz - Ff * ( y - this.eye ) / z ];
      const fr = canvas();
      g = fr.g;
      // the pavement: big wet flagstones from the kerb to our feet, the lights shining in them
      {
        const [ , y0 ] = Pf( 0, 0, ROAD.near ), y1 = H;
        const pg = g.createLinearGradient( 0, y0, 0, y1 );
        pg.addColorStop( 0, rgb( this.mix( slate, [ 70, 66, 62 ], 0.4 ) ) );
        pg.addColorStop( 1, rgb( this.mix( slate, [ 96, 92, 86 ], 0.45 ) ) );
        g.fillStyle = pg;
        g.fillRect( 0, y0, W, y1 - y0 );
        g.strokeStyle = rgb( this.mix( slate, [ 20, 20, 24 ], 0.5 ), 0.7 );
        g.lineWidth = 0.8;
        for ( let z = 1.2; z < ROAD.near; z += 1.2 ) { const [ , y ] = Pf( 0, 0, z ); g.beginPath(); g.moveTo( 0, y ); g.lineTo( W, y ); g.stroke(); }
        for ( let x = -40; x <= 40; x += 1.5 ) { const [ ax, ay ] = Pf( x, 0, 1.2 ), [ bx, by ] = Pf( x, 0, ROAD.near ); g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, by ); g.stroke(); }
        // the kerb
        poly( [ Pf( -200, 0, ROAD.near ), Pf( 200, 0, ROAD.near ), Pf( 200, 0.15, ROAD.near - 0.2 ), Pf( -200, 0.15, ROAD.near - 0.2 ) ], rgb( this.mix( slate, pale, 0.2 ) ) );
        // (kaizen) the wet flags holding the tower's gold and the palace's lights in long soft streaks, and a
        // few puddles shining
        const [ tx ] = Pf( TX, 0, TOWER.z );
        g.save();
        g.globalCompositeOperation = 'lighter';
        // (kaizen: no beams) one soft upright glimmer of the tower's gold under it
        {
          const sw = Ff * 9 / TOWER.z;
          g.save();
          g.translate( tx, y0 );
          g.scale( 1, ( y1 - y0 ) / ( sw * 1.2 ) );
          const sg2 = g.createRadialGradient( 0, 0, 0, 0, 0, sw * 1.2 );
          sg2.addColorStop( 0, rgb( gold, 0.12 ) );
          sg2.addColorStop( 1, rgb( gold, 0 ) );
          g.fillStyle = sg2;
          g.fillRect( -sw * 1.2, -sw * 1.2, sw * 2.4, sw * 2.4 );
          g.restore();
        }
        const pr = rr( 71 );
        for ( let q = 0; q < 9; q++ ) {
          const z = 3 + pr() * 22, x = ( pr() - 0.5 ) * W * z / Ff * 1.1, [ px, py ] = Pf( x, 0, z ), rw = Ff * ( 1.5 + pr() * 2.5 ) / z;
          const pg2 = g.createRadialGradient( px, py, 0, px, py, rw );
          pg2.addColorStop( 0, rgb( this.mix( steel, warmWin, 0.4 ), 0.1 ) );
          pg2.addColorStop( 1, rgb( steel, 0 ) );
          g.fillStyle = pg2;
          g.beginPath(); g.ellipse( px, py, rw, rw * 0.18, 0, 0, Math.PI * 2 ); g.fill();
        }
        g.restore();
      }
      // the bollards along the kerb, black, their tops catching the light
      for ( let x = -14; x <= 14; x += 2.4 ) {
        const z = ROAD.near - 0.6, [ bx, by ] = Pf( x, 0, z ), [ , ty ] = Pf( x, 0.9, z ), bw = Ff * 0.11 / z;
        g.fillStyle = rgb( [ 18, 18, 22 ] );
        g.fillRect( bx - bw, ty, bw * 2, by - ty );
        g.fillStyle = rgb( this.mix( steel, pale, 0.4 ), 0.8 );
        g.fillRect( bx - bw, ty, bw * 2, Math.max( 1, bw * 0.4 ) );
      }
      // (owner: the lamps done well) Westminster's Victorian lamps: a moulded plinth, the fluted column
      // tapering to its collars, the ladder bar, the lantern of tapering glass in its bars under a domed cap and
      // its finial; lit warm, its halo in the wet air, its light down the column and on the flags
      const lamp = ( x: number, z: number, hgt: number, Pp: ( x: number, y: number, z: number ) => [ number, number ], FF: number ) => {
        const s = FF / z, [ bx, by ] = Pp( x, 0, z ), [ , ty ] = Pp( x, hgt, z );
        const iron = this.mix( [ 18, 26, 24 ], night, 0.2 ), ironHi = this.mix( iron, warmWin, onL ? 0.25 : 0.06 );
        // where its light falls, the spot the lit copy is shown over
        {
          const [ lx0, ly0 ] = Pp( x, hgt + 0.37, z ), m = s * 2.7, x0 = Math.max( 0, Math.min( lx0, bx ) - m ), y0 = Math.max( 0, ly0 - m );
          this.lampRect = [ x0, y0, Math.min( W, Math.max( lx0, bx ) + m ) - x0, Math.min( H, by + Math.max( s * 0.6, ( by - ty ) * 0.5 ) + 2 ) - y0 ];
        }
        // its light on the wet flags, a soft long smear
        if ( onL ) {
          g.save();
          g.translate( bx, by );
          g.scale( 1, Math.max( 1, ( by - ty ) * 0.5 / ( s * 0.6 ) ) );
          const rg = g.createRadialGradient( 0, 0, 0, 0, 0, s * 0.6 );
          rg.addColorStop( 0, rgb( warmWin, 0.3 ) );
          rg.addColorStop( 1, rgb( warmWin, 0 ) );
          g.fillStyle = rg;
          g.fillRect( -s * 0.6, -s * 0.6, s * 1.2, s * 1.2 );
          g.restore();
        }
        // (owner: the lamp stood in the air) its foot on the flags: a dark contact shadow round the plinth and
        // the iron's own dark reflection in the wet stone below it
        {
          const sh = g.createRadialGradient( bx, by, 0, bx, by, s * 0.5 );
          sh.addColorStop( 0, 'rgba(0,0,0,0.55)' );
          sh.addColorStop( 0.6, 'rgba(0,0,0,0.25)' );
          sh.addColorStop( 1, 'rgba(0,0,0,0)' );
          g.save();
          g.translate( bx, by );
          g.scale( 1, 0.22 );
          g.translate( -bx, -by );
          g.fillStyle = sh;
          g.fillRect( bx - s * 0.5, by - s * 0.5, s * 1.0, s * 1.0 );
          g.restore();
          const rl = ( by - ty ) * 0.42, rf2 = g.createLinearGradient( 0, by, 0, by + rl );
          rf2.addColorStop( 0, rgb( this.mix( iron, [ 0, 0, 0 ], 0.3 ), 0.55 ) );
          rf2.addColorStop( 1, rgb( iron, 0 ) );
          g.fillStyle = rf2;
          g.beginPath(); g.moveTo( bx - s * 0.2, by ); g.lineTo( bx + s * 0.2, by ); g.lineTo( bx + s * 0.07, by + rl ); g.lineTo( bx - s * 0.07, by + rl ); g.closePath(); g.fill();
        }
        // the plinth: a stepped, moulded base (owner: the lamp slimmer, its foot and its top smaller)
        const [ , p1 ] = Pp( x, 0.35, z ), [ , p2 ] = Pp( x, 0.9, z );
        g.fillStyle = rgb( iron );
        g.fillRect( bx - s * 0.21, p1, s * 0.42, by - p1 );
        g.fillRect( bx - s * 0.15, p2, s * 0.3, p1 - p2 );
        g.fillStyle = rgb( ironHi );
        g.fillRect( bx - s * 0.21, p1, s * 0.42, Math.max( 0.6, s * 0.03 ) );
        g.fillRect( bx - s * 0.15, p2, s * 0.3, Math.max( 0.6, s * 0.03 ) );
        // the column, fluted, lit along one edge by its lantern
        const [ , cTop ] = Pp( x, hgt - 0.3, z );
        const cg = g.createLinearGradient( bx - s * 0.08, 0, bx + s * 0.08, 0 );
        cg.addColorStop( 0, rgb( iron ) );
        cg.addColorStop( 0.65, rgb( ironHi ) );
        cg.addColorStop( 1, rgb( iron ) );
        g.fillStyle = cg;
        g.beginPath(); g.moveTo( bx - s * 0.085, p2 ); g.lineTo( bx + s * 0.085, p2 ); g.lineTo( bx + s * 0.05, cTop ); g.lineTo( bx - s * 0.05, cTop ); g.closePath(); g.fill();
        g.strokeStyle = rgb( this.mix( iron, [ 0, 0, 0 ], 0.4 ), 0.8 );
        g.lineWidth = Math.max( 0.4, s * 0.012 );
        for ( const fx of [ -0.035, 0, 0.035 ] ) { g.beginPath(); g.moveTo( bx + s * fx * 1.6, p2 ); g.lineTo( bx + s * fx, cTop ); g.stroke(); }
        // the collars and the ladder bar
        for ( const yy of [ 2.0, 2.15, hgt - 0.35 ] ) { const [ , cy ] = Pp( x, yy, z ); g.fillStyle = rgb( ironHi ); g.fillRect( bx - s * 0.09, cy, s * 0.18, Math.max( 0.6, s * 0.04 ) ); }
        const [ , lb ] = Pp( x, hgt - 0.6, z );
        g.fillStyle = rgb( iron );
        g.fillRect( bx - s * 0.26, lb, s * 0.52, Math.max( 0.6, s * 0.03 ) );
        for ( const dx of [ -0.26, 0.23 ] ) { g.beginPath(); g.arc( bx + s * dx + s * 0.015, lb, Math.max( 0.6, s * 0.026 ), 0, Math.PI * 2 ); g.fill(); }
        // (owner: the lantern stood in the air) its seat: the column's neck narrowing over the top collar, a
        // ring, then the cup flaring out under the lantern's foot, all one piece of iron with the column
        {
          const [ , n0 ] = Pp( x, hgt - 0.3, z ), [ , n1 ] = Pp( x, hgt - 0.08, z ), [ , n2 ] = Pp( x, hgt + 0.08, z );
          g.fillStyle = rgb( iron );
          g.beginPath(); g.moveTo( bx - s * 0.055, n0 ); g.lineTo( bx + s * 0.055, n0 ); g.lineTo( bx + s * 0.038, n1 ); g.lineTo( bx - s * 0.038, n1 ); g.closePath(); g.fill();
          g.fillStyle = rgb( ironHi );
          g.fillRect( bx - s * 0.065, n1 - Math.max( 0.6, s * 0.025 ), s * 0.13, Math.max( 0.8, s * 0.035 ) );
          g.fillStyle = rgb( iron );
          g.beginPath(); g.moveTo( bx - s * 0.038, n1 ); g.lineTo( bx + s * 0.038, n1 ); g.quadraticCurveTo( bx + s * 0.045, n2 + s * 0.025, bx + s * 0.15, n2 ); g.lineTo( bx - s * 0.15, n2 ); g.quadraticCurveTo( bx - s * 0.045, n2 + s * 0.025, bx - s * 0.038, n1 ); g.closePath(); g.fill();
        }
        // the lantern: its halo, the glass tapering out to its top, the bars, the domed cap, the finial
        const [ lx, ly ] = Pp( x, hgt + 0.08 + 0.29, z );
        if ( onL ) {
          glow( lx, ly, s * 2.6, warmWin, 0.5 );
          glow( lx, ly, s * 0.75, [ 255, 246, 220 ], 0.6 );
        }
        const lw0 = s * 0.12, lw1 = s * 0.22, lh = s * 0.58;
        // its glass lit warm, or dark with the night's grey in it
        const gg2 = g.createLinearGradient( 0, ly - lh / 2, 0, ly + lh / 2 );
        gg2.addColorStop( 0, rgb( onL ? [ 255, 248, 222 ] : this.mix( [ 70, 74, 80 ], night, 0.3 ) ) );
        gg2.addColorStop( 1, rgb( onL ? [ 255, 214, 140 ] : this.mix( [ 40, 42, 46 ], night, 0.3 ) ) );
        g.fillStyle = gg2;
        g.beginPath(); g.moveTo( lx - lw0, ly + lh / 2 ); g.lineTo( lx + lw0, ly + lh / 2 ); g.lineTo( lx + lw1, ly - lh / 2 ); g.lineTo( lx - lw1, ly - lh / 2 ); g.closePath(); g.fill();
        g.strokeStyle = rgb( iron );
        g.lineWidth = Math.max( 0.5, s * 0.02 );
        g.beginPath(); g.moveTo( lx, ly + lh / 2 ); g.lineTo( lx, ly - lh / 2 ); g.moveTo( lx - lw0, ly + lh / 2 ); g.lineTo( lx - lw1, ly - lh / 2 ); g.moveTo( lx + lw0, ly + lh / 2 ); g.lineTo( lx + lw1, ly - lh / 2 ); g.stroke();
        g.fillStyle = rgb( iron );
        g.fillRect( lx - lw0 * 1.2, ly + lh / 2, lw0 * 2.4, Math.max( 1, s * 0.05 ) );
        g.fillRect( lx - lw1 * 1.15, ly - lh / 2 - s * 0.04, lw1 * 2.3, Math.max( 1, s * 0.045 ) );
        g.beginPath(); g.ellipse( lx, ly - lh / 2 - s * 0.04, lw1 * 0.95, s * 0.12, 0, Math.PI, 0 ); g.fill();
        g.beginPath(); g.moveTo( lx - s * 0.03, ly - lh / 2 - s * 0.15 ); g.lineTo( lx + s * 0.03, ly - lh / 2 - s * 0.15 ); g.lineTo( lx, ly - lh / 2 - s * 0.32 ); g.closePath(); g.fill();
        g.beginPath(); g.arc( lx, ly - lh / 2 - s * 0.18, Math.max( 0.6, s * 0.027 ), 0, Math.PI * 2 ); g.fill();
      };
      // the two boxes keep their own scale (owner)
      // (owner: both 100 px to the right and 20 px up; later 100 px back to the left)
      const FB = this.FB, PB = ( x: number, y: number, z: number ): [ number, number ] => [ this.vx + FB * x / z, this.hz - FB * ( y - this.eye ) / z - 20 ];
      // (kaizen) a red pillar box: (owner: drawn in 3D, its foot and all) a cylinder of cast iron seen a little
      // from above: the black plinth flaring at its foot with its top ring showing, the red body shaded round
      // its curve and lit from the lamps at the left, the door's outline, the gilt cipher, the white collection
      // plate, the posting slot under its hood, the cap's overhanging rim and its dome catching the light; its
      // contact shadow and its red smear on the wet flags
      {
        // (owner: the pillar box on the right of the screen) on the pavement at the right, clear of the words
        // (owner: then 60 px to the left; then nearer the road, smaller as it goes back; then right by the road)
        // at the kerb, on the pavement's own scale beside the bollards, at the same place on screen
        const px = 32.5, pz = ROAD.near - 0.8, rad = 0.3, s = Ff / pz;
        // a level circle of radius rr2 at height y: its centre on screen, its half width and half height
        const ring = ( y: number, rr2: number ): [ number, number, number, number ] => {
          const [ cx ] = Pf( px, y, pz ), [ , yf ] = Pf( px, y, pz - rr2 ), [ , yb ] = Pf( px, y, pz + rr2 );
          return [ cx, ( yf + yb ) / 2, s * rr2, Math.max( 0.5, ( yf - yb ) / 2 ) ];
        };
        // a band of the cylinder between two heights: the sides straight, the foot and the top the front arcs
        const band = ( y0: number, y1: number, rr2: number, f: string | CanvasGradient ) => {
          const [ cx, c0, ax, ay0 ] = ring( y0, rr2 ), [ , c1, , ay1 ] = ring( y1, rr2 );
          g.fillStyle = f;
          g.beginPath();
          g.ellipse( cx, c0, ax, ay0, 0, 0, Math.PI );
          g.lineTo( cx - ax, c1 );
          g.ellipse( cx, c1, ax, ay1, 0, Math.PI, 0, true );
          g.closePath();
          g.fill();
        };
        const round = ( c0: RGB, rr2: number ) => {
          const [ cx, , ax ] = ring( 0, rr2 ), gr = g.createLinearGradient( cx - ax, 0, cx + ax, 0 );
          gr.addColorStop( 0, rgb( this.mix( c0, [ 0, 0, 0 ], 0.55 ) ) );
          gr.addColorStop( 0.3, rgb( this.mix( c0, [ 255, 220, 190 ], 0.18 ) ) );
          gr.addColorStop( 0.45, rgb( c0 ) );
          gr.addColorStop( 0.85, rgb( this.mix( c0, [ 0, 0, 0 ], 0.45 ) ) );
          gr.addColorStop( 1, rgb( this.mix( c0, [ 0, 0, 0 ], 0.6 ) ) );
          return gr;
        };
        const body: RGB = this.mix( red, [ 200, 24, 30 ], 0.35 ), black: RGB = [ 26, 24, 26 ];
        // the contact shadow and the red smear in the wet stone
        {
          const [ cx, cy, ax, ay ] = ring( 0, rad * 1.5 );
          const sh = g.createRadialGradient( cx, cy, 0, cx, cy, ax );
          sh.addColorStop( 0, 'rgba(0,0,0,0.55)' );
          sh.addColorStop( 1, 'rgba(0,0,0,0)' );
          g.save(); g.translate( cx, cy ); g.scale( 1, ay / ax ); g.translate( -cx, -cy ); g.fillStyle = sh; g.fillRect( cx - ax, cy - ax, ax * 2, ax * 2 ); g.restore();
          const rl = s * 1.1, rg2 = g.createLinearGradient( 0, cy, 0, cy + rl );
          rg2.addColorStop( 0, rgb( body, 0.28 ) );
          rg2.addColorStop( 1, rgb( body, 0 ) );
          g.fillStyle = rg2;
          g.fillRect( cx - s * rad * 0.8, cy, s * rad * 1.6, rl );
        }
        // the plinth: black, flaring at the foot, its top ring round the body's foot
        band( 0, 0.06, rad * 1.18, round( black, rad * 1.18 ) );
        band( 0.06, 0.24, rad * 1.1, round( black, rad * 1.1 ) );
        { const [ cx, cy, ax, ay ] = ring( 0.24, rad * 1.1 ); g.fillStyle = rgb( this.mix( black, [ 120, 116, 110 ], 0.35 ) ); g.beginPath(); g.ellipse( cx, cy, ax, ay, 0, 0, Math.PI * 2 ); g.fill(); }
        // the body, its waist a touch narrower than the cap
        band( 0.24, 1.42, rad, round( body, rad ) );
        // the door's outline, a little in from the edges, and its hinge side darker
        g.strokeStyle = rgb( this.mix( body, [ 0, 0, 0 ], 0.45 ), 0.8 );
        g.lineWidth = Math.max( 0.5, s * 0.012 );
        {
          const [ cx, c0, ax, ay0 ] = ring( 0.36, rad ), [ , c1, , ay1 ] = ring( 1.0, rad );
          g.beginPath();
          g.ellipse( cx, c0, ax * 0.62, ay0 * 0.98, 0, Math.PI * 0.18, Math.PI * 0.82 );
          g.moveTo( cx - ax * 0.62 * Math.cos( Math.PI * 0.18 ), c0 + ay0 * Math.sin( Math.PI * 0.18 ) );
          g.lineTo( cx - ax * 0.62 * Math.cos( Math.PI * 0.18 ), c1 + ay1 * Math.sin( Math.PI * 0.18 ) );
          g.ellipse( cx, c1, ax * 0.62, ay1 * 0.98, 0, Math.PI * 0.82, Math.PI * 0.18, true );
          g.lineTo( cx + ax * 0.62 * Math.cos( Math.PI * 0.18 ), c0 + ay0 * Math.sin( Math.PI * 0.18 ) );
          g.stroke();
        }
        // the gilt cipher and crown on the door, when it is large enough to read
        if ( s * 0.075 >= 3 ) {
          const [ cx, cy ] = Pf( px, 0.78, pz - rad ), fs = s * 0.075;
          g.fillStyle = rgb( [ 214, 172, 80 ] );
          g.font = `700 ${ fs.toFixed( 1 ) }px Georgia, 'Times New Roman', serif`;
          g.textAlign = 'center';
          g.textBaseline = 'middle';
          g.fillText( 'EIIR', cx, cy );
          g.beginPath(); g.moveTo( cx - fs * 0.5, cy - fs * 0.7 ); g.lineTo( cx - fs * 0.55, cy - fs * 1.25 ); g.lineTo( cx - fs * 0.2, cy - fs * 0.95 ); g.lineTo( cx, cy - fs * 1.4 ); g.lineTo( cx + fs * 0.2, cy - fs * 0.95 ); g.lineTo( cx + fs * 0.55, cy - fs * 1.25 ); g.lineTo( cx + fs * 0.5, cy - fs * 0.7 ); g.closePath(); g.fill();
        }
        // the collection plate, white, curving with the body
        band( 1.04, 1.16, rad * 1.005, rgb( [ 236, 232, 218 ] ) );
        {
          // only its middle shows white: cover the sides back with the body's curve
          const [ cx, , ax ] = ring( 1.1, rad ), [ , c0, , ay0 ] = ring( 1.04, rad ), [ , c1, , ay1 ] = ring( 1.16, rad );
          g.save();
          g.beginPath(); g.rect( cx - ax * 1.05, c1 - ay1 - 2, ax * 0.68, c0 + ay0 - c1 + ay1 + 4 ); g.rect( cx + ax * 0.37, c1 - ay1 - 2, ax * 0.68, c0 + ay0 - c1 + ay1 + 4 ); g.clip();
          band( 1.03, 1.17, rad * 1.01, round( body, rad ) );
          g.restore();
        }
        // the slot under its little hood
        {
          const [ cx, cy, ax, ay ] = ring( 1.27, rad * 1.01 );
          g.strokeStyle = rgb( [ 14, 8, 10 ] );
          g.lineWidth = Math.max( 1, s * 0.035 );
          g.beginPath(); g.ellipse( cx, cy, ax, ay, 0, Math.PI * 0.32, Math.PI * 0.68 ); g.stroke();
          const [ , hy, , hay ] = ring( 1.31, rad * 1.04 );
          g.strokeStyle = rgb( this.mix( body, [ 255, 220, 190 ], 0.2 ) );
          g.lineWidth = Math.max( 0.6, s * 0.02 );
          g.beginPath(); g.ellipse( cx, hy, ax * 1.03, hay, 0, Math.PI * 0.3, Math.PI * 0.7 ); g.stroke();
        }
        // the cap: its overhanging rim, then the dome over it, lit on its shoulder
        band( 1.42, 1.5, rad * 1.14, round( this.mix( body, [ 0, 0, 0 ], 0.15 ), rad * 1.14 ) );
        {
          const [ cx, cy, ax, ay ] = ring( 1.5, rad * 1.1 ), dh = s * 0.17;
          const dg = g.createRadialGradient( cx - ax * 0.35, cy - ay - dh * 0.6, 0, cx, cy, ax * 1.2 );
          dg.addColorStop( 0, rgb( this.mix( body, [ 255, 220, 190 ], 0.35 ) ) );
          dg.addColorStop( 0.5, rgb( body ) );
          dg.addColorStop( 1, rgb( this.mix( body, [ 0, 0, 0 ], 0.5 ) ) );
          g.fillStyle = dg;
          g.beginPath();
          g.ellipse( cx, cy, ax, ay + dh, 0, Math.PI, 0 );
          g.ellipse( cx, cy, ax, ay, 0, 0, Math.PI );
          g.closePath();
          g.fill();
          // the rim's lit edge
          g.strokeStyle = rgb( this.mix( body, [ 255, 220, 190 ], 0.3 ), 0.8 );
          g.lineWidth = Math.max( 0.5, s * 0.012 );
          g.beginPath(); g.ellipse( cx, cy, ax, ay, 0, 0, Math.PI ); g.stroke();
        }
      }
      // (owner: the street lamp and the telephone box side by side, on the same ground) the lamp just left of
      // the box, standing where the box stands, at its scale; (owner: in their real sizes) a Victorian post
      // about 4.2 m to its finial beside the 2.5 m box
      lamp( BOX.x - 1.45, BOX.z, 3.0, PB, FB );
      // the K6 telephone box: red cast iron, its domed roof with the crown, the lit TELEPHONE signs, the door's
      // glazing in its bars, the warm light inside; its front and its right side toward us
      {
        const { x: X, z: Z } = BOX, w = 0.92, d = 0.92, h = 2.5, s = FB / Z;
        const front = this.mix( [ 192, 20, 32 ], red, 0.15 ), sideC = this.mix( front, [ 40, 4, 8 ], 0.42 ), dark = this.mix( front, [ 30, 2, 6 ], 0.55 );
        const x0 = X - w / 2, x1 = X + w / 2;
        // its glow and reflection on the wet flags
        const [ rbx, rby ] = PB( X, 0, Z );
        {
          const [ gcx, gcy ] = PB( X, 0.05, Z - 0.4 ), x0 = Math.max( 0, Math.min( PB( X - w / 2, 0, Z )[ 0 ], gcx - s * 1.3 ) - 2 ), y0 = Math.max( 0, PB( X, h + 0.7, Z )[ 1 ] - 2 );
          this.boxRect = [ x0, y0, Math.min( W, Math.max( PB( X + w / 2, 0, Z + d )[ 0 ], gcx + s * 1.3 ) + 2 ) - x0, Math.min( H, Math.max( rby + s * 0.6 * 2.2, gcy + s * 1.3 ) + 2 ) - y0 ];
        }
        g.save();
        g.translate( rbx, rby );
        g.scale( 1, 2.2 );
        const rf = g.createRadialGradient( 0, 0, 0, 0, 0, s * 0.6 );
        rf.addColorStop( 0, rgb( red, onB ? 0.32 : 0.14 ) );
        rf.addColorStop( 1, rgb( red, 0 ) );
        g.fillStyle = rf;
        g.fillRect( -s * 0.6, -s * 0.6, s * 1.2, s * 1.2 );
        g.restore();
        // (owner: the telephone box in 3D, drawn well, its colours) the K6 as it stands: a concrete step under
        // it; the cast iron body in its own red, the front lit from the street and the side in shade, the corner
        // posts standing proud with their lit edges; the door and the side glazed in three by eight panes, the
        // warm light inside, or dark glass when it is off; the brass handle;
        // the lit TELEPHONE signs on the front and the side
        const hiC = this.mix( front, [ 255, 206, 176 ], 0.28 ), stepC = this.mix( [ 112, 110, 106 ], night, 0.35 );
        const faceF = ( y0: number, y1: number ) => {
          const fg = g.createLinearGradient( 0, PB( 0, y1, Z )[ 1 ], 0, PB( 0, y0, Z )[ 1 ] );
          fg.addColorStop( 0, rgb( this.mix( front, [ 30, 2, 6 ], 0.25 ) ) );
          fg.addColorStop( 0.6, rgb( front ) );
          fg.addColorStop( 1, rgb( this.mix( front, [ 255, 190, 150 ], 0.12 ) ) );
          return fg;
        };
        const faceS = ( y0: number, y1: number ) => {
          const sg2 = g.createLinearGradient( 0, PB( 0, y1, Z )[ 1 ], 0, PB( 0, y0, Z )[ 1 ] );
          sg2.addColorStop( 0, rgb( this.mix( sideC, [ 0, 0, 0 ], 0.25 ) ) );
          sg2.addColorStop( 1, rgb( this.mix( sideC, front, 0.25 ) ) );
          return sg2;
        };
        const quadF = ( xa: number, xb: number, ya: number, yb: number, zz: number, f: string | CanvasGradient ) => poly( [ PB( xa, ya, zz ), PB( xb, ya, zz ), PB( xb, yb, zz ), PB( xa, yb, zz ) ], f );
        const quadS = ( xs: number, za: number, zb: number, ya: number, yb: number, f: string | CanvasGradient ) => poly( [ PB( xs, ya, za ), PB( xs, ya, zb ), PB( xs, yb, zb ), PB( xs, yb, za ) ], f );
        // the concrete step
        quadS( x1 + 0.06, Z - 0.06, Z + d + 0.06, 0, 0.1, rgb( this.mix( stepC, [ 0, 0, 0 ], 0.3 ) ) );
        quadF( x0 - 0.06, x1 + 0.06, 0, 0.1, Z - 0.06, rgb( stepC ) );
        poly( [ PB( x0 - 0.06, 0.1, Z - 0.06 ), PB( x1 + 0.06, 0.1, Z - 0.06 ), PB( x1 + 0.06, 0.1, Z + d + 0.06 ), PB( x0 - 0.06, 0.1, Z + d + 0.06 ) ], rgb( this.mix( stepC, pale, 0.15 ) ) );
        // the body: the side in shade, then the front
        quadS( x1, Z, Z + d, 0.1, h, faceS( 0.1, h ) );
        quadF( x0, x1, 0.1, h, Z, faceF( 0.1, h ) );
        // the glazing: the lit inside (or the dark glass), the bars (owner: nothing drawn inside)
        const gx0 = x0 + 0.13, gx1 = x1 - 0.13, gz0 = Z + 0.13, gz1 = Z + d - 0.13, gy0 = 0.42, gy1 = 2.02;
        const glass = ( k: number ) => {
          const gl = g.createLinearGradient( 0, PB( 0, gy1, Z )[ 1 ], 0, PB( 0, gy0, Z )[ 1 ] );
          gl.addColorStop( 0, rgb( onB ? this.mix( [ 255, 236, 190 ], [ 200, 150, 90 ], k ) : this.mix( [ 52, 50, 58 ], night, k * 0.4 ) ) );
          gl.addColorStop( 1, rgb( onB ? this.mix( [ 214, 160, 96 ], [ 150, 104, 60 ], k ) : this.mix( [ 26, 24, 30 ], night, k * 0.4 ) ) );
          return gl;
        };
        quadS( x1 + 0.005, gz0, gz1, gy0, gy1, glass( 0.35 ) );
        quadF( gx0, gx1, gy0, gy1, Z - 0.005, glass( 0 ) );
        // the bars: a frame's own red, catching the light along their tops
        g.lineCap = 'butt';
        g.strokeStyle = rgb( this.mix( front, [ 30, 2, 6 ], 0.1 ) );
        g.lineWidth = Math.max( 1, s * 0.035 );
        for ( let q = 1; q < 3; q++ ) {
          const x = gx0 + ( gx1 - gx0 ) * q / 3, [ ax, ay ] = PB( x, gy0, Z - 0.01 ), [ , by ] = PB( x, gy1, Z - 0.01 );
          g.beginPath(); g.moveTo( ax, ay ); g.lineTo( ax, by ); g.stroke();
        }
        for ( let q = 1; q < 8; q++ ) {
          const y = gy0 + ( gy1 - gy0 ) * q / 8, [ ax, ay ] = PB( gx0, y, Z - 0.01 ), [ bx ] = PB( gx1, y, Z - 0.01 );
          g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, ay ); g.stroke();
        }
        g.strokeStyle = rgb( this.mix( sideC, [ 0, 0, 0 ], 0.1 ) );
        g.lineWidth = Math.max( 0.8, s * 0.03 );
        for ( let q = 1; q < 3; q++ ) {
          const zz = gz0 + ( gz1 - gz0 ) * q / 3, [ ax, ay ] = PB( x1 + 0.01, gy0, zz ), [ bx, by ] = PB( x1 + 0.01, gy1, zz );
          g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, by ); g.stroke();
        }
        for ( let q = 1; q < 8; q++ ) {
          const y = gy0 + ( gy1 - gy0 ) * q / 8, [ ax, ay ] = PB( x1 + 0.01, y, gz0 ), [ bx, by ] = PB( x1 + 0.01, y, gz1 );
          g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, by ); g.stroke();
        }
        // the glazing's lit edges under each bar on the front
        g.strokeStyle = rgb( hiC, 0.45 );
        g.lineWidth = Math.max( 0.4, s * 0.01 );
        for ( let q = 1; q < 8; q++ ) {
          const y = gy0 + ( gy1 - gy0 ) * q / 8, [ ax, ay ] = PB( gx0, y + 0.012, Z - 0.012 ), [ bx ] = PB( gx1, y + 0.012, Z - 0.012 );
          g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, ay ); g.stroke();
        }
        // the door's outline and its brass handle
        g.strokeStyle = rgb( dark );
        g.lineWidth = Math.max( 0.8, s * 0.02 );
        { const [ ax, ay ] = PB( x0 + 0.08, 0.15, Z - 0.012 ), [ bx, by ] = PB( x1 - 0.08, 2.1, Z - 0.012 ); g.strokeRect( ax, by, bx - ax, ay - by ); }
        quadF( x1 - 0.12, x1 - 0.095, 1.0, 1.3, Z - 0.02, rgb( [ 200, 160, 90 ] ) );
        // the corner posts standing proud, their lit edges
        quadF( x0 - 0.01, x0 + 0.1, 0.1, h, Z - 0.03, faceF( 0.1, h ) );
        quadF( x1 - 0.1, x1 + 0.01, 0.1, h, Z - 0.03, faceF( 0.1, h ) );
        quadS( x1 + 0.01, Z - 0.03, Z + 0.1, 0.1, h, faceS( 0.1, h ) );
        quadS( x1 + 0.01, Z + d - 0.1, Z + d, 0.1, h, faceS( 0.1, h ) );
        g.strokeStyle = rgb( hiC, 0.85 );
        g.lineWidth = Math.max( 0.6, s * 0.014 );
        for ( const xe of [ x0 + 0.1, x1 + 0.01 ] ) { const [ ax, ay ] = PB( xe, 0.1, Z - 0.03 ), [ , by ] = PB( xe, h, Z - 0.03 ); g.beginPath(); g.moveTo( ax, ay ); g.lineTo( ax, by ); g.stroke(); }
        // the signs over the door and on the side, lit white
        const sy0 = 2.12, sy1 = 2.32;
        quadF( x0 + 0.1, x1 - 0.1, sy0, sy1, Z - 0.02, rgb( onB ? [ 250, 248, 236 ] : [ 138, 134, 126 ] ) );
        quadS( x1 + 0.01, Z + 0.1, Z + d - 0.1, sy0, sy1, rgb( onB ? [ 230, 226, 214 ] : [ 112, 108, 102 ] ) );
        {
          const [ ax, ay ] = PB( x0 + 0.12, sy0 + 0.03, Z - 0.03 ), [ bx, by ] = PB( x1 - 0.12, sy1 - 0.03, Z - 0.03 );
          const tw = bx - ax, th = ay - by;
          g.fillStyle = rgb( [ 30, 26, 26 ] );
          g.font = `600 ${ Math.max( 4, th * 0.9 ).toFixed( 1 ) }px Georgia, 'Times New Roman', serif`;
          g.textAlign = 'center';
          g.textBaseline = 'middle';
          const m = g.measureText( 'TELEPHONE' ).width;
          g.save();
          g.translate( ( ax + bx ) / 2, ( ay + by ) / 2 );
          g.scale( Math.min( 1, tw * 0.92 / Math.max( 1, m ) ), 1 );
          g.fillText( 'TELEPHONE', 0, 0 );
          g.restore();
          // the side's word too, carried along the side in its perspective
          const [ sx0, syA ] = PB( x1 + 0.012, ( sy0 + sy1 ) / 2, Z + 0.14 ), [ sx1, syB ] = PB( x1 + 0.012, ( sy0 + sy1 ) / 2, Z + d - 0.14 );
          const len = Math.hypot( sx1 - sx0, syB - syA );
          if ( len > 6 ) {
            g.save();
            g.translate( ( sx0 + sx1 ) / 2, ( syA + syB ) / 2 );
            g.transform( ( sx1 - sx0 ) / len, ( syB - syA ) / len, 0, 1, 0, 0 );
            g.scale( Math.min( 1, len * 0.92 / Math.max( 1, m ) ), 0.9 );
            g.fillStyle = rgb( [ 40, 34, 34 ] );
            g.fillText( 'TELEPHONE', 0, 0 );
            g.restore();
          }
        }
        // the inside's light glowing through the panes a little into the wet air
        if ( onB ) {
          const [ cx, cy ] = PB( X, 1.2, Z - 0.05 );
          g.save();
          g.globalCompositeOperation = 'lighter';
          glow( cx, cy, s * 1.1, [ 255, 210, 150 ], 0.12 );
          g.restore();
        }
        // (owner: the box's top was broken; then its top looked unfinished) the K6's roof whole: the cornice
        // standing out round the top; the domed roof as one curved surface over the square, its edge on each
        // face a shallow arch, shaded cell by cell by how it turns to the lamps at the left; under each arch the
        // tympanum with the crown in relief; the arches' moulded rims catching the light
        const ry = h + 0.07, rise = 0.17, ox = w / 2 + 0.04, oz = d / 2 + 0.04, zc = Z + d / 2;
        // the cornice
        quadS( x1 + 0.04, Z - 0.04, Z + d + 0.04, h - 0.03, ry, rgb( this.mix( sideC, hiC, 0.12 ) ) );
        quadF( x0 - 0.04, x1 + 0.04, h - 0.03, ry, Z - 0.04, rgb( this.mix( front, hiC, 0.2 ) ) );
        g.strokeStyle = rgb( dark, 0.8 );
        g.lineWidth = Math.max( 0.5, s * 0.012 );
        { const [ ax, ay ] = PB( x0 - 0.04, h - 0.03, Z - 0.04 ), [ bx ] = PB( x1 + 0.04, h - 0.03, Z - 0.04 ), [ cx, cy ] = PB( x1 + 0.04, h - 0.03, Z + d + 0.04 ); g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, ay ); g.lineTo( cx, cy ); g.stroke(); }
        // the dome: y = ry + rise (2 - u^2 - v^2) over the square, its edges the four arches; drawn far to near
        const domeY = ( u: number, v: number ) => ry + rise * ( 2 - u * u - v * v );
        const at3 = ( u: number, v: number ): [ number, number ] => PB( X + u * ox, domeY( u, v ), zc + v * oz );
        const Lx = -0.45, Ly = 0.35, Lz = -0.82, n = 10, cells: [ number, number, number ][] = [];
        for ( let i = 0; i < n; i++ ) { for ( let j = 0; j < n; j++ ) { const u = -1 + ( i + 0.5 ) * 2 / n, v = -1 + ( j + 0.5 ) * 2 / n; cells.push( [ i, j, Math.hypot( X + u * ox, zc + v * oz ) ] ); } }
        cells.sort( ( a2, b2 ) => b2[ 2 ] - a2[ 2 ] );
        g.lineJoin = 'round';
        for ( const [ i, j ] of cells ) {
          const u0 = -1 + i * 2 / n, u1 = u0 + 2 / n, v0 = -1 + j * 2 / n, v1 = v0 + 2 / n, um = ( u0 + u1 ) / 2, vm = ( v0 + v1 ) / 2;
          const nx = 2 * rise * um / ox, nz = 2 * rise * vm / oz, nl = Math.hypot( nx, 1, nz );
          const lit = Math.max( 0, ( nx * Lx + Ly + nz * Lz ) / nl );
          const col = rgb( this.mix( this.mix( front, [ 30, 2, 6 ], 0.45 ), this.mix( front, hiC, 0.3 ), Math.min( 1, lit * 1.1 ) ) );
          const pts = [ at3( u0, v0 ), at3( u1, v0 ), at3( u1, v1 ), at3( u0, v1 ) ];
          poly( pts, col );
          g.strokeStyle = col;
          g.lineWidth = 0.6;
          g.beginPath(); pts.forEach( ( [ x, y ], k ) => k ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.closePath(); g.stroke();
        }
        // the tympana under the arches: the side, then the front
        const archS: [ number, number ][] = [], archF: [ number, number ][] = [];
        for ( let q = 0; q <= 20; q++ ) {
          const t = -1 + q / 10;
          archS.push( PB( x1 + 0.04, ry + rise * ( 1 - t * t ), zc + t * oz ) );
          archF.push( PB( X + t * ox, ry + rise * ( 1 - t * t ), Z - 0.04 ) );
        }
        poly( archS.concat( [ PB( x1 + 0.04, ry, Z + d + 0.04 ), PB( x1 + 0.04, ry, Z - 0.04 ) ] ), rgb( this.mix( sideC, [ 0, 0, 0 ], 0.1 ) ) );
        {
          const tg = g.createLinearGradient( 0, PB( 0, ry + rise, Z )[ 1 ], 0, PB( 0, ry, Z )[ 1 ] );
          tg.addColorStop( 0, rgb( this.mix( front, [ 30, 2, 6 ], 0.2 ) ) );
          tg.addColorStop( 1, rgb( front ) );
          poly( archF.concat( [ PB( x1 + 0.04, ry, Z - 0.04 ), PB( x0 - 0.04, ry, Z - 0.04 ) ] ), tg );
        }
        // the arches' moulded rims: a lit edge with its shadow under it
        for ( const [ arc, c0 ] of [ [ archS, this.mix( sideC, hiC, 0.35 ) ], [ archF, hiC ] ] as [ [ number, number ][], RGB ][] ) {
          g.strokeStyle = rgb( dark, 0.7 );
          g.lineWidth = Math.max( 0.6, s * 0.022 );
          g.beginPath(); arc.forEach( ( [ x, y ], k ) => k ? g.lineTo( x, y + Math.max( 0.6, s * 0.02 ) ) : g.moveTo( x, y + Math.max( 0.6, s * 0.02 ) ) ); g.stroke();
          g.strokeStyle = rgb( c0 );
          g.lineWidth = Math.max( 0.7, s * 0.028 );
          g.beginPath(); arc.forEach( ( [ x, y ], k ) => k ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.stroke();
        }
        // the crown in relief on each tympanum: its band, three points with their pearls, a cross on the middle
        const crown = ( cx: number, cy: number, k: number, sk: number, skY: number, col: RGB ) => {
          g.save();
          g.translate( cx, cy );
          g.transform( sk, skY, 0, 1, 0, 0 );
          g.fillStyle = rgb( col );
          g.beginPath();
          g.moveTo( -k * 0.13, k * 0.04 ); g.lineTo( -k * 0.15, -k * 0.07 ); g.lineTo( -k * 0.07, -k * 0.03 ); g.lineTo( 0, -k * 0.1 );
          g.lineTo( k * 0.07, -k * 0.03 ); g.lineTo( k * 0.15, -k * 0.07 ); g.lineTo( k * 0.13, k * 0.04 ); g.closePath(); g.fill();
          g.fillRect( -k * 0.14, k * 0.035, k * 0.28, k * 0.03 );
          for ( const px2 of [ -0.15, 0, 0.15 ] ) { g.beginPath(); g.arc( k * px2, px2 ? -k * 0.075 : -k * 0.105, Math.max( 0.4, k * 0.016 ), 0, Math.PI * 2 ); g.fill(); }
          g.fillRect( -Math.max( 0.3, k * 0.006 ), -k * 0.15, Math.max( 0.6, k * 0.012 ), k * 0.05 );
          g.restore();
        };
        // (owner: the crown ran over its arch) smaller, inside the tympanum with a margin all round
        {
          const kc = s * 0.55, cyM = ry + 0.075;
          const [ kx, ky ] = PB( X, cyM, Z - 0.05 );
          crown( kx, ky, kc, 1, 0, this.mix( front, [ 255, 206, 176 ], 0.3 ) );
          const [ sxa, sya ] = PB( x1 + 0.05, cyM, zc - 0.2 ), [ sxb, syb ] = PB( x1 + 0.05, cyM, zc + 0.2 ), [ scx, scy ] = PB( x1 + 0.05, cyM, zc );
          const sk = ( sxb - sxa ) / ( s * 0.4 ), skY = ( syb - sya ) / ( s * 0.4 );
          if ( Math.abs( sk ) > 0.15 ) { crown( scx, scy, kc, sk, skY, this.mix( sideC, [ 255, 206, 176 ], 0.22 ) ); }
        }
        // the light from inside falling on the flags before it
        if ( onB ) { glow( PB( X, 0.05, Z - 0.4 )[ 0 ], PB( X, 0.05, Z - 0.4 )[ 1 ], s * 1.3, [ 255, 220, 160 ], 0.2 ); }
      }
      if ( !onL && !onB ) {
        this.front = fr.c;
        // the lit copies keep only the spot round the lamp and the box
        const [ lx, ly, lw, lh ] = this.lampRect, [ bx, by, bw, bh ] = this.boxRect;
        const ux = Math.floor( Math.min( lx, bx ) ), uy = Math.floor( Math.min( ly, by ) );
        this.litRect = [ ux, uy, Math.ceil( Math.max( lx + lw, bx + bw ) ) - ux, Math.ceil( Math.max( ly + lh, by + bh ) ) - uy ];
        continue;
      }
      const [ ux, uy, uw, uh ] = this.litRect, crop = document.createElement( 'canvas' );
      crop.width = Math.max( 1, Math.ceil( uw * scale ) );
      crop.height = Math.max( 1, Math.ceil( uh * scale ) );
      crop.getContext( '2d' )!.drawImage( fr.c, ux * scale, uy * scale, uw * scale, uh * scale, 0, 0, uw * scale, uh * scale );
      if ( onL && onB ) { this.litBoth = crop; } else if ( onL ) { this.litLamp = crop; } else { this.litBox = crop; }
    }
    void W0;
  }

  /**
   * Where the row at the right begins at depth z (m): 60 px past the palace's end on screen (owner: the room at
   * the palace's right). The row there begins so that its first end face, 6 m deep, keeps that room clear.
   */
  private rightStart( TX: number, z: number ): number {
    return ( this.F * TX / TOWER.z + this.F * PALACE.len / PALACE.z + 60 ) * z / this.F;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The five colours of the theme's circle: London's slate, pillar box red, rose, steel blue and pale. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 59, 58, 69 ], [ 238, 75, 91 ], [ 232, 135, 144 ], [ 127, 163, 198 ], [ 221, 223, 234 ] ];
  }
}
